/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from '../services/axiosInstance';
import { IAsset, ICollection, ICategory, ITemplate } from '../types/damTypes';
import TemplateFieldsForm from './TemplateFieldsForm';
import AssetHistory from './AssetHistory';
import { API_BASE_URL } from '../config';
import { getTemplateUrlValidationErrors } from '../utils/templateFieldValidation';
import {
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Chip,
    Divider,
    Stack,
    Collapse,
    InputAdornment,
    Tooltip,
    Snackbar,
    Alert,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableRow,
    TableCell,
    LinearProgress,
    Menu,
    MenuItem,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import BugReportIcon from '@mui/icons-material/BugReport';
import UploadIcon from '@mui/icons-material/Upload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import VisualAssetProfilePanel from '../modules/knowledgeHub/VisualAssetProfilePanel';
import RecipeCorrectionApprovalBar from '../modules/recipeCorrection/ApprovalBar';
import VersionUploadModal from './VersionUploadModal';
import { IPdfTemplate } from '../types/pdfTemplateTypes';
import { getActivePdfTemplates, downloadPdf } from '../services/pdfTemplateService';
import { filterTemplatesForSelection } from '../utils/pdfTemplateMenuUtils';
import {
  ActiveDownloadTemplate,
  BuiltInDownloadTemplateId,
  downloadAssetsWithBuiltInTemplate,
  fetchActiveDownloadTemplates as fetchActiveImageDownloadTemplates,
  getAvailableBuiltInDownloadTemplates,
  runCustomAssetDownloadTemplate,
} from '../utils/assetDownloadMenuUtils';
import VideoPlayer from './VideoPlayer';
import AssetWordPressPanel from './AssetWordPressPanel';
import RelationImpactDialog from '../modules/referenceImpact/RelationImpactDialog';
import {
  getWordPressArchiveUnpublishImpacts,
  requestWordPressArchiveUnpublish,
  WordPressArchiveUnpublishImpact,
} from '../modules/wordpressPublishing/archiveUnpublishGuard';

// Temp definition
type IAssetVersion = any; 

const VERSION_THUMBNAIL_UNSUPPORTED_EXTENSIONS = new Set(['.avi', '.mov', '.mp4', '.psd']);
const RECIPE_CONVERTIBLE_IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png']);

type VersionThumbnailImageProps = {
  src: string | null;
  fallbackSrc?: string | null;
  alt: string;
  placeholderLabel: string;
};

function getFileExtension(filePath?: string | null): string {
  const normalized = String(filePath || '').split('?')[0].trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  return dotIndex >= 0 ? normalized.slice(dotIndex) : '';
}

function canUseVersionThumbnailEndpoint(filePath?: string | null): boolean {
  const extension = getFileExtension(filePath);
  return !extension || !VERSION_THUMBNAIL_UNSUPPORTED_EXTENSIONS.has(extension);
}

function isJpgOrPngAsset(asset?: IAsset | null): boolean {
  if (!asset) return false;
  const extension = getFileExtension(asset.fileName || asset.filePath);
  if (RECIPE_CONVERTIBLE_IMAGE_EXTENSIONS.has(extension)) return true;

  const fileType = String(asset.fileType || '').toLowerCase();
  return fileType === 'image/jpeg' || fileType === 'image/jpg' || fileType === 'image/png';
}

function appendCacheKey(url: string, cacheKey?: string | number | null, paramName = 'v'): string {
  if (!url) return url;
  if (cacheKey === undefined || cacheKey === null || String(cacheKey).trim() === '') return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${paramName}=${encodeURIComponent(String(cacheKey))}`;
}

function buildStaticThumbnailUrl(relativePath?: string, cacheKey?: string | number | null): string | null {
  if (!relativePath) return null;
  return appendCacheKey(`${API_BASE_URL}${relativePath}`, cacheKey);
}

function buildVersionThumbnailUrl(assetId: string, versionId: string, cacheKey?: string | number | null): string {
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (cacheKey !== undefined && cacheKey !== null && String(cacheKey).trim() !== '') {
    params.set('t', String(cacheKey));
  }
  const query = params.toString();
  return `${API_BASE_URL}/api/assets/${assetId}/versions/${versionId}/thumbnail${query ? `?${query}` : ''}`;
}

const VERSION_THUMBNAIL_IMAGE_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const VersionThumbnailImage: React.FC<VersionThumbnailImageProps> = ({
  src,
  fallbackSrc = null,
  alt,
  placeholderLabel,
}) => {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(src);
  const [failed, setFailed] = useState<boolean>(!src);

  useEffect(() => {
    setResolvedSrc(src);
    setFailed(!src);
  }, [src]);

  if (!resolvedSrc || failed) {
    return (
      <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
        {placeholderLabel}
      </Typography>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      style={VERSION_THUMBNAIL_IMAGE_STYLE}
      onError={() => {
        if (fallbackSrc && resolvedSrc !== fallbackSrc) {
          setResolvedSrc(fallbackSrc);
          return;
        }
        setFailed(true);
      }}
    />
  );
};

interface AssetDetailsProps {
  assetId: string | null;
  allCategories: ICategory[]; // Add prop for all categories
  onAssetDeleted: (assetId: string) => void; // Add prop to handle asset deletion
  onClose?: () => void; // NY: Lades till, optionell för bakåtkompatibilitet med andra användningar
  onAssetUpdate?: () => void; // NY: Lades till, optionell
  collections?: ICollection[]; // NY: Lades till, optionell
  templates?: ITemplate[]; // NY: Lades till, optionell
  isAdmin?: boolean; // Whether current user has admin role
  canUpdate?: boolean;
  canDelete?: boolean;
  canManageVersions?: boolean;
  canFavorite?: boolean;
  canDownload?: boolean;
  canValidooSync?: boolean;
  canShare?: boolean;
  // Lightbox navigation props
  assetIds?: string[]; // List of all visible asset IDs for navigation
  onAssetChange?: (newAssetId: string) => void; // Callback when navigating to another asset
  layoutVariant?: 'panel' | 'fullPage';
  onOpenFullPage?: (assetId: string) => void;
  onOpenAiImageStudioForRecipeAsset?: (assetId: string) => void;
  onOpenAiImageStudioForEditAsset?: (assetId: string) => void;
  onOpenRecipeCorrection?: (assetId: string, options?: { snapshot?: boolean }) => void;
  readOnly?: boolean;
}

const AssetDetails: React.FC<AssetDetailsProps> = ({ 
  assetId, 
  allCategories, 
  onAssetDeleted, 
  onClose, // NY
  onAssetUpdate, // NY
  collections, // NY
  templates: propTemplates, // NY, byt namn för att undvika konflikt med state
  isAdmin = false, // Default to false if not provided
  canUpdate = false,
  canDelete = false,
  canManageVersions = false,
  canFavorite = false,
  canDownload = false,
  canValidooSync = false,
  canShare = false,
  assetIds = [],
  onAssetChange,
  layoutVariant = 'panel',
  onOpenFullPage,
  onOpenAiImageStudioForRecipeAsset,
  onOpenAiImageStudioForEditAsset,
  onOpenRecipeCorrection,
  readOnly = false,
}) => {
  const [asset, setAsset] = useState<IAsset | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableMetadata, setEditableMetadata] = useState<Partial<IAsset>>({});
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]); // State for selected categories in edit mode
  const [viewingVersion, setViewingVersion] = useState<IAssetVersion | null>(null); // State for viewing a specific version
  const [restoring, setRestoring] = useState(false); // State for restore process
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<IAssetVersion | null>(null);
  const [togglingFavorite, setTogglingFavorite] = useState(false); // State for toggling favorite
  const [togglingCollection, setTogglingCollection] = useState(false); // State for toggling collection membership
  const [isDeleting, setIsDeleting] = useState(false); // State for delete operation
  const [recipeDeleteImpactDialogOpen, setRecipeDeleteImpactDialogOpen] = useState(false);
  const [deleteWordPressImpacts, setDeleteWordPressImpacts] = useState<WordPressArchiveUnpublishImpact[]>([]);
  // Add state for expanded categories
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  // Add state for tag input
  const [tagInput, setTagInput] = useState('');

  // State for syncing with external systems
  const [isValidooSyncing, setIsValidooSyncing] = useState(false);
  const [isDabasSyncing, setIsDabasSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
    duration?: number;
  } | null>(null);
  const [isValidooSynced, setIsValidooSynced] = useState(false);
  
  // Combined syncing state for UI that needs to know if ANY sync is in progress
  const isSyncing = isValidooSyncing || isDabasSyncing;
  const handleToggleSkipValidooSync = async (checked: boolean) => {
    if (!asset) return;
    try {
      await axiosInstance.put(`/api/assets/${asset._id}`, { skipValidooSync: checked });
      setAsset(prev => prev ? { ...prev, skipValidooSync: checked } as any : prev);
      setSyncMessage({ type: 'success', text: checked ? 'Produkten exkluderas från Validoo-synk' : 'Produkten inkluderas i Validoo-synk igen', duration: 4000 });
      if (onAssetUpdate) onAssetUpdate();
    } catch (err: any) {
      setSyncMessage({ type: 'error', text: err.response?.data?.message || err.message, duration: 6000 });
    }
  };

  const handleToggleSkipDabasSync = async (checked: boolean) => {
    if (!asset) return;
    try {
      await axiosInstance.put(`/api/assets/${asset._id}`, { skipDabasSync: checked });
      setAsset(prev => prev ? { ...prev, skipDabasSync: checked } as any : prev);
      setSyncMessage({ type: 'success', text: checked ? 'Produkten exkluderas från Dabas-synk' : 'Produkten inkluderas i Dabas-synk igen', duration: 4000 });
      if (onAssetUpdate) onAssetUpdate();
    } catch (err: any) {
      setSyncMessage({ type: 'error', text: err.response?.data?.message || err.message, duration: 6000 });
    }
  };

  // State for Collections within AssetDetails
  const [allCollections, setAllCollections] = useState<ICollection[]>([]);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [collectionError, setCollectionError] = useState<string | null>(null);

  // Add state for templates and template fields
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate | null>(null);
  const [templateFieldValues, setTemplateFieldValues] = useState<Record<string, any>>({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const templateUrlValidationErrors = useMemo(() => {
    if (!isEditing || !selectedTemplate) {
      return [];
    }

    return getTemplateUrlValidationErrors(selectedTemplate, templateFieldValues, {
      showUploadFields: false,
      showEditFields: true,
    });
  }, [isEditing, selectedTemplate, templateFieldValues]);
  const hasInvalidTemplateUrls = templateUrlValidationErrors.length > 0;
  
  // PDF templates menu state
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeDownloadTemplates, setActiveDownloadTemplates] = useState<ActiveDownloadTemplate[]>([]);
  const [loadingDownloadTemplates, setLoadingDownloadTemplates] = useState(false);
  const [downloadTemplatesError, setDownloadTemplatesError] = useState<string | null>(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);
  const [pdfTemplates, setPdfTemplates] = useState<IPdfTemplate[]>([]);
  const [loadingPdfTemplates, setLoadingPdfTemplates] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const visiblePdfTemplates = useMemo(
    () => filterTemplatesForSelection(pdfTemplates || [], 1),
    [pdfTemplates]
  );
  
  // Dialogs for sync-all confirmation and progress
  const [confirmSyncAllOpen, setConfirmSyncAllOpen] = useState(false);
  const [syncAllProgressOpen, setSyncAllProgressOpen] = useState(false);
  const [syncAllResults, setSyncAllResults] = useState<{
    totalAssets: number;
    successCount: number;
    failedCount: number;
    success: boolean;
    message: string;
    failures: { assetId: string; message: string }[];
    successAssetIds?: string[];
  } | null>(null);
  const [showSuccessList, setShowSuccessList] = useState(false);

  // Derived: does this asset belong to a template with Validoo integration?
  const hasValidooIntegration = useMemo(() => {
    try {
      if (!asset) return false;
      const availableTemplates = (propTemplates && propTemplates.length > 0) ? propTemplates : templates;
      const matching = availableTemplates.find(t => t.categoryIds?.some(id => asset.categories?.includes(id)));
      return Boolean(matching?.externalIntegrations?.some(i => i.system === 'validoo' && i.enabled));
    } catch {
      return false;
    }
  }, [asset, propTemplates, templates]);

  // Derived: does this asset belong to a template with Dabas integration?
  const hasDabasIntegration = useMemo(() => {
    try {
      if (!asset) return false;
      const availableTemplates = (propTemplates && propTemplates.length > 0) ? propTemplates : templates;
      const matching = availableTemplates.find(t => t.categoryIds?.some(id => asset.categories?.includes(id)));
      return Boolean(matching?.externalIntegrations?.some(i => i.system === 'dabas' && i.enabled));
    } catch {
      return false;
    }
  }, [asset, propTemplates, templates]);

  const refreshValidooSyncStatus = async (id: string) => {
    try {
      const resp = await axiosInstance.get(`/api/activities/asset/${id}`, {
        params: { activityType: 'validoo_sync', limit: 1, offset: 0 }
      });
      const has = Array.isArray(resp.data?.activities) && resp.data.activities.length > 0;
      setIsValidooSynced(has);
    } catch {}
  };

  useEffect(() => {
    if (!asset?._id || !hasValidooIntegration) {
      setIsValidooSynced(false);
      return;
    }
    refreshValidooSyncStatus(asset._id);
  }, [asset?._id, hasValidooIntegration]);

  // Add AI editable fields
  const [editableAIDescription, setEditableAIDescription] = useState('');
  const [editableAITags, setEditableAITags] = useState<string[]>([]);

  // Add this new state at the top of the component, around lines 45-90
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  // Store material stock status
  const [stockLevel, setStockLevel] = useState<number | null>(null);
  const [loadingStock, setLoadingStock] = useState(false);

  // State for version upload modal  
  const [isVersionUploadOpen, setIsVersionUploadOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [recipeCategoryDialogOpen, setRecipeCategoryDialogOpen] = useState(false);
  const [selectedRecipeSubCategoryId, setSelectedRecipeSubCategoryId] = useState('');
  const [convertingToRecipe, setConvertingToRecipe] = useState(false);
  
  // State for drag and drop
  const [isDragOver, setIsDragOver] = useState(false);
  
  // State for tabs
  const [activeTab, setActiveTab] = useState(0);
  // State for history tabs
  const [historyActiveTab, setHistoryActiveTab] = useState(0);
  // EXIF tab state
  const [exifData, setExifData] = useState<{ exif: Record<string,string>; iptc: Record<string,string>; xmp: Record<string,string>; clippingPath?: { present: boolean; name?: string } } | null>(null);
  const [loadingExif, setLoadingExif] = useState(false);

  // Auto-load EXIF when the Exif tab is opened the first time
  useEffect(() => {
    const load = async () => {
      if (!asset?._id || loadingExif || exifData) return;
      try {
        setLoadingExif(true);
        const resp = await axiosInstance.get(`/api/assets/${asset._id}/metadata`);
        setExifData(resp.data);
      } catch (e) {
        // Silent fail; user can retry with button
        console.warn('Failed to auto-load EXIF');
      } finally {
        setLoadingExif(false);
      }
    };
    if (historyActiveTab === 1 && asset?.fileType?.startsWith('image/')) {
      load();
    }
  }, [historyActiveTab, asset?._id]);
  // Fullscreen preview dialog state
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState<string | null>(null);
  const [fullscreenLoading, setFullscreenLoading] = useState(false);

  // Load larger image for fullscreen view
  const openFullscreen = async () => {
    setFullscreenOpen(true);
    
    // If we already have a fullscreen image or are loading, skip
    if (fullscreenImageUrl || fullscreenLoading) return;
    
    if (!asset) return;
    
    setFullscreenLoading(true);
    try {
      // Try to get a larger preview (1920px) for fullscreen
      const { getPreviewUrl } = await import('../services/previewService');
      const largeUrl = await getPreviewUrl(asset._id, 'medium', 1920);
      const finalUrl = `${largeUrl}${largeUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
      setFullscreenImageUrl(finalUrl);
      setDisplayedFullscreenImageUrl(finalUrl);
      setFullscreenAssetId(asset._id);
    } catch {
      // Fallback to existing preview
      setFullscreenImageUrl(previewImageUrl);
      setDisplayedFullscreenImageUrl(previewImageUrl);
      setFullscreenAssetId(asset?._id || null);
    }
    setFullscreenLoading(false);
  };

  // Lightbox navigation
  const currentIndex = assetId && assetIds.length > 0 ? assetIds.indexOf(assetId) : -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < assetIds.length - 1;

  const currentVersionCacheKey = asset?.modifiedAt || asset?.uploadedAt || asset?._id || null;
  const currentVersionEndpointAllowed = canUseVersionThumbnailEndpoint(asset?.filePath);
  const currentVersionFallbackSrc = asset?.previewPaths?.thumbnail && asset?._id && currentVersionEndpointAllowed
    ? buildVersionThumbnailUrl(asset._id, asset._id, currentVersionCacheKey)
    : null;
  const currentVersionThumbnailSrc = asset?._id
    ? (
      buildStaticThumbnailUrl(asset.previewPaths?.thumbnail, currentVersionCacheKey)
      || (currentVersionEndpointAllowed
        ? buildVersionThumbnailUrl(asset._id, asset._id, currentVersionCacheKey)
        : null)
    )
    : null;

  // Track which asset the current fullscreen image belongs to
  const [fullscreenAssetId, setFullscreenAssetId] = useState<string | null>(null);
  const [displayedFullscreenImageUrl, setDisplayedFullscreenImageUrl] = useState<string | null>(null);

  const navigateToPrevious = () => {
    if (hasPrevious && onAssetChange) {
      const prevId = assetIds[currentIndex - 1];
      onAssetChange(prevId);
    }
  };

  const navigateToNext = () => {
    if (hasNext && onAssetChange) {
      const nextId = assetIds[currentIndex + 1];
      onAssetChange(nextId);
    }
  };

  // Keyboard navigation for lightbox
  React.useEffect(() => {
    if (!fullscreenOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        navigateToPrevious();
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        navigateToNext();
      } else if (e.key === 'Escape') {
        setFullscreenOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenOpen, hasPrevious, hasNext, currentIndex]);

  // Auto-load large image when navigating in lightbox
  React.useEffect(() => {
    if (!fullscreenOpen || !assetId) return;
    
    // If we already have an image for this asset, skip
    if (fullscreenImageUrl && fullscreenAssetId === assetId) return;

    const loadLargeImage = async () => {
      setFullscreenLoading(true);
      try {
        const { getPreviewUrl } = await import('../services/previewService');
        const largeUrl = await getPreviewUrl(assetId, 'medium', 1920);
        // Only update if we're still on the same asset
        const finalUrl = `${largeUrl}${largeUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
        setFullscreenImageUrl(finalUrl);
        setDisplayedFullscreenImageUrl(finalUrl);
        setFullscreenAssetId(assetId);
      } catch {
        if (previewImageUrl) {
          setDisplayedFullscreenImageUrl(previewImageUrl);
        }
        setFullscreenAssetId(assetId);
      }
      setFullscreenLoading(false);
    };

    loadLargeImage();
  }, [fullscreenOpen, assetId]);

  // Store material ("butiksmaterial") classification.
  // Preferred: ID-based (any descendant under root category id).
  // Safety: keep legacy direct-name match as fallback to avoid regressions.
  const STORE_MATERIAL_ROOT_CATEGORY_IDS = ['682c6c2078cc40e17f2354cf'];
  const LEGACY_STORE_CATEGORY_NAMES = new Set([
    'Butiksmaterial',
    'Skyltmaterial',
    'Prisskiltar',
    'Kampanjmaterial',
    'Broschyrer',
    'Menyer',
  ]);

  const getParentCategoryId = (cat: any): string | null => {
    const p: any = cat?.parentCategory;
    if (!p) return null;
    if (typeof p === 'string') return p;
    if (typeof p === 'object' && typeof p._id === 'string') return p._id;
    return null;
  };

  const isStoreAsset = (asset: IAsset): boolean => {
    if (!asset.categories || asset.categories.length === 0) return false;

    const byId = new Map<string, any>();
    const parentById = new Map<string, string | null>();
    for (const c of allCategories || []) {
      if (!c || !(c as any)._id) continue;
      byId.set((c as any)._id, c);
      parentById.set((c as any)._id, getParentCategoryId(c));
    }

    const validRootIds = new Set<string>();
    for (const id of STORE_MATERIAL_ROOT_CATEGORY_IDS) {
      if (byId.has(id)) validRootIds.add(id);
    }

    // Preferred: descendant under root
    if (validRootIds.size > 0) {
      for (const categoryId of asset.categories) {
        let current: string | null | undefined = categoryId;
        const visited = new Set<string>();
        while (current) {
          if (validRootIds.has(current)) return true;
          if (visited.has(current)) break;
          visited.add(current);
          current = parentById.get(current) || null;
        }
      }
    }

    // Fallback: legacy direct-name match (previous behavior)
    for (const categoryId of asset.categories) {
      const category = byId.get(categoryId);
      if (category && LEGACY_STORE_CATEGORY_NAMES.has(String(category.name || ''))) return true;
    }

    return false;
  };

  // Helper function to determine if asset is under Produkter or Vin categories
  const getAssetPdfType = (asset: IAsset): 'products' | 'wine' | null => {
    if (!asset.categories || asset.categories.length === 0) return null;
    
    // Check all ancestor categories to determine type
    const checkCategoryAndAncestors = (categoryId: string): 'products' | 'wine' | null => {
      const category = allCategories.find(cat => cat._id === categoryId);
      if (!category) return null;
      
      // Check if this category or any ancestor is Produkter or Vin
      if (category.name === 'Produkter' || category.name === 'Products') return 'products';
      if (category.name === 'Vin' || category.name === 'Wine') return 'wine';
      
      // Check parent (parentCategory can be string or object)
      const parentId = typeof category.parentCategory === 'string' 
        ? category.parentCategory 
        : (category.parentCategory as any)?._id;
      if (parentId) {
        return checkCategoryAndAncestors(parentId);
      }
      
      return null;
    };
    
    for (const categoryId of asset.categories) {
      const type = checkCategoryAndAncestors(categoryId);
      if (type) return type;
    }
    
    return null;
  };

  const isAssetUnderNamedCategoryRoot = (asset: IAsset | null, rootNameLower: string): boolean => {
    if (!asset?.categories || asset.categories.length === 0) return false;

    const matchesNamedRoot = (categoryId: string, visited = new Set<string>()): boolean => {
      if (!categoryId || visited.has(categoryId)) return false;
      visited.add(categoryId);

      const category = allCategories.find(cat => cat._id === categoryId);
      if (!category) return false;

      if (String(category.name || '').trim().toLowerCase() === rootNameLower) return true;

      const parentId = typeof category.parentCategory === 'string'
        ? category.parentCategory
        : (category.parentCategory as any)?._id;

      return parentId ? matchesNamedRoot(String(parentId), visited) : false;
    };

    return asset.categories.some(categoryId => matchesNamedRoot(String(categoryId)));
  };

  const normalizeMetaValue = (value: any): string => {
    if (value === undefined || value === null) return '';
    if (Array.isArray(value)) {
      const first = value.map(v => String(v).trim()).find(v => v);
      return first || '';
    }
    return String(value).trim();
  };

  const getWineProductNameFromMetadata = (metadata: Record<string, any> | undefined): string => {
    if (!metadata) return '';
    const direct = normalizeMetaValue((metadata as any)['Produktnamn_vin_id']);
    if (direct) return direct;
    for (const [k, v] of Object.entries(metadata)) {
      if (k.toLowerCase().includes('produktnamn_vin_id')) {
        const vv = normalizeMetaValue(v);
        if (vv) return vv;
      }
    }
    return '';
  };

  const getProductsProductNameFromMetadata = (metadata: Record<string, any> | undefined): string => {
    if (!metadata) return '';
    const direct =
      normalizeMetaValue((metadata as any).produktnamn) ||
      normalizeMetaValue((metadata as any).Produktnamn);
    if (direct) return direct;
    for (const [k, v] of Object.entries(metadata)) {
      const kl = k.toLowerCase();
      if (kl.includes('produktnamn') && !kl.includes('vin_id')) {
        const vv = normalizeMetaValue(v);
        if (vv) return vv;
      }
    }
    return '';
  };

  const getRecipeNameFromMetadata = (metadata: Record<string, any> | undefined): string => {
    if (!metadata) return '';
    const direct = normalizeMetaValue((metadata as any).receptnamn_recept_id);
    if (direct) return direct;
    for (const [k, v] of Object.entries(metadata)) {
      const kl = k.toLowerCase();
      if (kl === 'receptnamn_recept_id') {
        const vv = normalizeMetaValue(v);
        if (vv) return vv;
      }
    }
    return '';
  };

  const getAutoTitleForAsset = (a: IAsset | null): string => {
    if (!a) return '';
    if (isAssetUnderNamedCategoryRoot(a, 'recept')) {
      const recipeName = getRecipeNameFromMetadata((a as any).metadata);
      if (recipeName) return recipeName;
    }
    const type = getAssetPdfType(a);
    if (type === 'wine') {
      const pn = getWineProductNameFromMetadata((a as any).metadata);
      if (pn) return pn;
    } else if (type === 'products') {
      const pn = getProductsProductNameFromMetadata((a as any).metadata);
      if (pn) return pn;
    }
    return (a.title || '').trim() || decodeFileName(a.fileName);
  };

  // Handle PDF menu open - fetch available templates
  const handlePdfMenuOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setPdfMenuAnchor(event.currentTarget);
    
    if (!asset) return;
    
    setLoadingPdfTemplates(true);
    try {
      const assetType = getAssetPdfType(asset);
      const templates = await getActivePdfTemplates(assetType || undefined);
      setPdfTemplates(templates);
    } catch (err) {
      console.error('Failed to load PDF templates:', err);
      setPdfTemplates([]);
    } finally {
      setLoadingPdfTemplates(false);
    }
  };

  // Handle PDF menu close
  const handlePdfMenuClose = () => {
    setPdfMenuAnchor(null);
  };

  // Handle PDF download
  const handlePdfDownload = async (templateId: string) => {
    if (!asset) return;
    
    handlePdfMenuClose();
    setDownloadingPdf(true);
    
    try {
      await downloadPdf(templateId, asset._id);
    } catch (err: any) {
      console.error('Failed to download PDF:', err);
      setSyncMessage({ 
        type: 'error', 
        text: err.message || 'Kunde inte generera PDF', 
        duration: 5000 
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Function to fetch stock level for store assets
  const fetchStockLevel = async (assetId: string) => {
    try {
      setLoadingStock(true);
      const response = await axiosInstance.get(`/api/cart/store-items`);
      // Fix: API returns assetId, not _id
      const storeItem = response.data.find((item: any) => item.assetId === assetId);
      setStockLevel(storeItem ? storeItem.stockLevel : 0);
    } catch (error) {
      console.error('Error fetching stock level:', error);
      setStockLevel(null);
    } finally {
      setLoadingStock(false);
    }
  };

  // Function to fetch asset details
  const fetchAssetDetails = async (id: string) => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get<IAsset>(`/api/assets/${id}`);
        console.log('Fetched asset data:', response.data);
        
        setAsset(response.data);
        setEditableMetadata({
            title: response.data.title,
            description: response.data.description,
            tags: response.data.tags,
            categories: response.data.categories,
            qbankId: response.data.qbankId,
        });
        
        // Initialize AI fields
        setEditableAIDescription(response.data.aiDescription || '');
        setEditableAITags(response.data.aiTags || []);
        
        // Decide file kind (vector vs raster)
        const isVectorKind = !!(response.data.fileType?.includes('postscript') ||
                               response.data.fileType?.includes('illustrator') ||
                               response.data.fileType?.includes('pdf') ||
                               (response.data.fileName || '').toLowerCase().endsWith('.ai') ||
                               (response.data.fileName || '').toLowerCase().endsWith('.eps'));

        // For vectors: use preview endpoint with server-side recovery/generation (token in query)
        if (isVectorKind) {
          // For the large preview, fetch token immediately (single asset)
          try {
            const { getPreviewUrl } = await import('../services/previewService');
            const signed = await getPreviewUrl(id, 'medium', 800);
            const ts = Date.now();
            setPreviewImageUrl(`${signed}${signed.includes('?') ? '&' : '?'}t=${ts}`);
          } catch {
            const token = localStorage.getItem('authToken');
            const ts = Date.now();
            setPreviewImageUrl(`${API_BASE_URL}/api/assets/${id}/preview${token ? `?token=${encodeURIComponent(token)}&t=${ts}` : `?t=${ts}`}`);
          }
        }
        // For raster images: try static previewPaths with small retry window
        else if (response.data.previewPaths?.medium || response.data.previewPaths?.thumbnail) {
          const tryLoadPreview = async (attempt: number): Promise<void> => {
            const maxAttempts = 4; // total tries
            const delayMs = 300;   // backoff delay
            try {
              const previewPath = response.data.previewPaths?.medium || response.data.previewPaths?.thumbnail;
              if (previewPath) {
                const cacheBuster = `?t=${Date.now()}`;
                const directUrl = `${API_BASE_URL}${previewPath}${cacheBuster}`;
                const previewResponse = await axiosInstance.get(directUrl, { responseType: 'blob' });
                const imageUrl = URL.createObjectURL(previewResponse.data);
                setPreviewImageUrl(imageUrl);
              }
            } catch (err: any) {
              // Retry only on 404 / network errors; otherwise fall back
              const status = err?.response?.status;
              if ((status === 404 || !status) && attempt < 3) {
                await new Promise(r => setTimeout(r, delayMs));
                return tryLoadPreview(attempt + 1);
              }
              console.error('Error loading preview image:', err);
              const previewPath = response.data.previewPaths?.medium || response.data.previewPaths?.thumbnail;
              if (previewPath) {
                const token = localStorage.getItem('authToken');
                const cacheBuster = `t=${Date.now()}`;
                const tempUrl = `${API_BASE_URL}${previewPath}?token=${token}&${cacheBuster}`;
                setPreviewImageUrl(tempUrl);
              }
            }
          };
          await tryLoadPreview(0);
        }
        // Fallback for image files without preview paths - try to use the original file
        else if (response.data.fileType?.startsWith('image/')) {
          try {
            const downloadResponse = await axiosInstance.get(`/api/assets/${id}/download`, {
              responseType: 'blob'
            });
            const imageUrl = URL.createObjectURL(downloadResponse.data);
            setPreviewImageUrl(imageUrl);
          } catch (downloadErr) {
            console.error('Error loading original image:', downloadErr);
          }
        }
        
        // Extract template field values from metadata if they exist
        if (response.data.metadata) {
          console.log('=== ASSET METADATA DEBUG ===');
          console.log('Raw metadata from API:', response.data.metadata);
          console.log('Metadata keys:', Object.keys(response.data.metadata));
          console.log('Metadata values:', Object.values(response.data.metadata));
          console.log('Setting template field values from metadata:', response.data.metadata);
          // Ensure we have a proper copy, not a reference
          const templateValues = JSON.parse(JSON.stringify(response.data.metadata || {}));
          console.log('Template field values set to:', templateValues);
          setTemplateFieldValues(templateValues);
        } else {
          console.log('No metadata found in asset, resetting template field values');
          setTemplateFieldValues({});
        }
        
        setSelectedCategoryIds(response.data.categories || []); // Initialize selected categories state
        
        // Find matching template - prioritize propTemplates if available
        const availableTemplates = propTemplates && propTemplates.length > 0 ? propTemplates : templates;
        console.log('Available templates for matching:', availableTemplates.length);
        
        if (availableTemplates.length > 0 && response.data.categories && response.data.categories.length > 0) {
          const matchingTemplate = availableTemplates.find(template => 
            template.categoryIds.some(catId => response.data.categories.includes(catId))
          );
          
          if (matchingTemplate) {
            console.log('=== TEMPLATE MATCHING DEBUG ===');
            console.log('Found matching template for asset:', matchingTemplate.name);
            console.log('Template fields:', matchingTemplate.fields);
            console.log('Template field IDs and names:', matchingTemplate.fields.map(f => ({ id: f.id, name: f.name, label: f.label })));
            setSelectedTemplate(matchingTemplate);
          } else {
            console.log('No matching template found for categories:', response.data.categories);
            // Try to find a default template
            const defaultTemplate = availableTemplates.find(template => template.isDefault);
            if (defaultTemplate) {
              console.log('Using default template instead:', defaultTemplate.name);
              setSelectedTemplate(defaultTemplate);
            } else {
              console.log('No default template found either, clearing selected template');
              setSelectedTemplate(null);
            }
          }
        } else if (availableTemplates.length > 0) {
          console.log('No categories assigned to asset, looking for default template');
          const defaultTemplate = availableTemplates.find(template => template.isDefault);
          if (defaultTemplate) {
            console.log('Using default template:', defaultTemplate.name);
            setSelectedTemplate(defaultTemplate);
          } else {
            setSelectedTemplate(null);
          }
        }

        // Check if this is a store asset and fetch stock level
        if (isStoreAsset(response.data)) {
          await fetchStockLevel(id);
        } else {
          setStockLevel(null);
        }
      } catch (err: any) {
        console.error('Error fetching asset details:', err);
        setError(err.message);
        setAsset(null);
        setEditableMetadata({});
        setViewingVersion(null);
        setTemplateFieldValues({});
        setStockLevel(null);
      } finally {
        setLoading(false);
      }
  };

   // Function to fetch all collections
  const fetchAllCollections = async () => {
    try {
      setLoadingCollections(true);
      setCollectionError(null);
      const response = await axiosInstance.get<ICollection[]>('/api/collections');
      setAllCollections(response.data);
    } catch (err: any) {
      setCollectionError(err.message);
    } finally {
      setLoadingCollections(false);
    }
  };

  // Add function to fetch templates
  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await axiosInstance.get<ITemplate[]>('/api/templates');
      setTemplates(response.data);
      
      // If we have asset categories, find matching template
      if (asset && asset.categories && asset.categories.length > 0) {
        const matchingTemplate = response.data.find(template => 
          template.categoryIds.some(catId => asset.categories.includes(catId))
        );
        
        if (matchingTemplate) {
          setSelectedTemplate(matchingTemplate);
          console.log('Found matching template:', matchingTemplate.name);
        }
      }
    } catch (err: any) {
      console.error('Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    // Don't clear fullscreen image here - let the lightbox useEffect handle smooth transitions
    
    if (!assetId) {
      setAsset(null);
      setIsEditing(false);
      setEditableMetadata({});
      setSaveMessage(null);
      setViewingVersion(null); // Clear viewing version when no asset is selected
      setSelectedTemplate(null); // Clear selected template when no asset is selected
      setFullscreenImageUrl(null);
      setFullscreenAssetId(null);
      setDisplayedFullscreenImageUrl(null);
      
      // Rensa preview-URL om sådan finns
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
        setPreviewImageUrl(null);
      }
      
      return;
    }

    fetchAssetDetails(assetId); // Use the new function
    // No need to fetch collections here, fetch them once on mount

    // Cleanup-funktion
    return () => {
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
  }, [assetId]);

   // Fetch all collections once on component mount
  useEffect(() => {
    fetchAllCollections();
    fetchTemplates();
  }, []);

  // NEW: Use propTemplates if provided
  useEffect(() => {
    if (propTemplates && propTemplates.length > 0) {
      console.log('Using provided templates from props:', propTemplates);
      setTemplates(propTemplates);
      
      // If we have asset categories, find matching template from propTemplates
      if (asset && asset.categories && asset.categories.length > 0) {
        const matchingTemplate = propTemplates.find(template => 
          template.categoryIds.some(catId => asset.categories.includes(catId))
        );
        
        if (matchingTemplate) {
          console.log('Found matching template from props:', matchingTemplate.name);
          setSelectedTemplate(matchingTemplate);
        }
      }
    }
  }, [propTemplates, asset]);

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditableMetadata(prev => ({
      ...prev,
      [name]: name === 'tags' ? value.split(',').map(tag => tag.trim()) : value,
    }));
  };

  // Handle category checkbox change
  const handleCategoryChange = (categoryId: string, checked: boolean) => {
      setSelectedCategoryIds(prevIds => {
          if (checked) {
              // Add category ID if checked and not already present
              return prevIds.includes(categoryId) ? prevIds : [...prevIds, categoryId];
          } else {
              // Remove category ID if unchecked
              return prevIds.filter(id => id !== categoryId);
          }
      });
  };

  // Category tree helpers (supports >2 levels)
  const getCategoryParentId = (cat: ICategory): string | null => {
    const p: any = (cat as any).parentCategory;
    if (!p) return null;
    if (typeof p === 'string') return p;
    if (typeof p === 'object' && typeof p._id === 'string') return p._id;
    return null;
  };

  const childrenByParentId = React.useMemo(() => {
    const map: Record<string, ICategory[]> = {};
    (allCategories || []).forEach((cat) => {
      const parentId = getCategoryParentId(cat);
      if (!parentId) return;
      if (!map[parentId]) map[parentId] = [];
      map[parentId].push(cat);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv')));
    return map;
  }, [allCategories]);

  const recipeRootCategory = React.useMemo(() => {
    return (allCategories || []).find((cat) => {
      const name = String(cat.name || '').trim().toLocaleLowerCase('sv-SE');
      return name === 'recept' && !getCategoryParentId(cat);
    }) || null;
  }, [allCategories]);

  const recipeSubcategories = React.useMemo(() => {
    if (!recipeRootCategory) return [];
    return childrenByParentId[recipeRootCategory._id] || [];
  }, [childrenByParentId, recipeRootCategory]);

  const currentRecipeSubCategoryId = React.useMemo(() => {
    if (!asset?.categories || recipeSubcategories.length === 0) return '';
    return recipeSubcategories.find((category) => asset.categories.includes(category._id))?._id || '';
  }, [asset?.categories, recipeSubcategories]);

  const getDescendantCategoryIds = React.useCallback((parentId: string): string[] => {
    const result: string[] = [];
    const stack: string[] = [...((childrenByParentId[parentId] || []).map(c => c._id))];
    while (stack.length > 0) {
      const id = stack.pop() as string;
      result.push(id);
      const kids = childrenByParentId[id] || [];
      kids.forEach(k => stack.push(k._id));
    }
    return result;
  }, [childrenByParentId]);

  const hasSelectedDescendants = React.useCallback((parentId: string): boolean => {
    const descendants = getDescendantCategoryIds(parentId);
    if (descendants.length === 0) return false;
    return descendants.some(id => selectedCategoryIds.includes(id));
  }, [getDescendantCategoryIds, selectedCategoryIds]);

  const hasChildCategories = React.useCallback((categoryId: string): boolean => {
    return (childrenByParentId[categoryId] || []).length > 0;
  }, [childrenByParentId]);

  const renderCategoryNode = React.useCallback((category: ICategory, depth: number) => {
    const isChecked = selectedCategoryIds.includes(category._id);
    const indeterminate = !isChecked && hasSelectedDescendants(category._id);
    const children = childrenByParentId[category._id] || [];
    const hasChildren = children.length > 0;
    const isExpanded = !!expandedCategories[category._id];

    return (
      <Box key={category._id}>
        <ListItem sx={{ p: 0, pl: depth === 0 ? 0 : 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                indeterminate={indeterminate}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    handleCategoryChange(category._id, true);
                  } else {
                    // Remove this category and all descendants
                    handleCategoryChange(category._id, false);
                    getDescendantCategoryIds(category._id).forEach(id => handleCategoryChange(id, false));
                  }
                }}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontWeight: depth === 0 ? 'bold' : 500 }}>
                {category.name}
              </Typography>
            }
          />

          {hasChildren && (
            <IconButton size="small" onClick={() => toggleCategoryExpansion(category._id)}>
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 3 }}>
              {children.map((child) => renderCategoryNode(child, depth + 1))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  }, [childrenByParentId, expandedCategories, getDescendantCategoryIds, handleCategoryChange, hasSelectedDescendants, selectedCategoryIds, toggleCategoryExpansion]);

  const getUploaderDisplayName = useCallback((u: any) => {
    if (!u) return null;
    const first = (u.firstName || '').toString().trim();
    const last = (u.lastName || '').toString().trim();
    const fullName = `${first} ${last}`.trim();
    const legacyName = (u.name || u.username || '').toString().trim();
    const email = (u.email || '').toString().trim();
    return fullName || legacyName || email || null;
  }, []);

  const uploaderLabel = React.useMemo(
    () => getUploaderDisplayName((asset as any)?.uploadedBy),
    [asset, getUploaderDisplayName]
  );

  // Toggle category expansion (function declaration avoids "used before declaration" in callbacks)
  function toggleCategoryExpansion(categoryId: string) {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  }

  // Handle tag input change
  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };

  // Handle tag input keydown (for Enter key)
  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (!editableMetadata.tags?.includes(newTag)) {
        setEditableMetadata(prev => ({
          ...prev,
          tags: [...(prev.tags || []), newTag]
        }));
      }
      setTagInput('');
    }
  };

  // Remove tag from editable tags
  const handleRemoveTag = (tagToRemove: string) => {
    setEditableMetadata(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle AI tag input keydown
  const handleAITagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (!editableAITags.includes(newTag)) {
        setEditableAITags([...editableAITags, newTag]);
      }
      setTagInput('');
    }
  };

  // Remove AI tag
  const handleRemoveAITag = (tagToRemove: string) => {
    setEditableAITags(prevTags => prevTags.filter(tag => tag !== tagToRemove));
  };

  // Add template fields change handler
  const handleTemplateFieldChange = (fieldId: string, value: any) => {
    console.log('Template field change:', fieldId, value);
    
    // Check if the value is a Date object
    const processedValue = value instanceof Date ? value.toISOString() : value;

    // Auto-sync title with Produktnamn for VIN/Produkter while editing
    try {
      if (asset && isEditing && selectedTemplate) {
        const type = getAssetPdfType(asset);
        const field = (selectedTemplate.fields || []).find((f: any) => f && (f.id === fieldId || f.name === fieldId));
        const fieldNameLower = String(field?.name || '').toLowerCase();
        const fieldIdLower = String(fieldId || '').toLowerCase();
        const labelLower = String(field?.label || '').toLowerCase();
        const looksLikeProductName =
          (type === 'wine' && (fieldNameLower === 'produktnamn_vin_id' || fieldIdLower === 'produktnamn_vin_id' || labelLower === 'produktnamn')) ||
          (type === 'products' && (fieldNameLower === 'produktnamn' || fieldIdLower === 'produktnamn' || labelLower === 'produktnamn'));

        const nextValue = typeof processedValue === 'string' ? processedValue.trim() : String(processedValue || '').trim();
        if (looksLikeProductName && nextValue) {
          setEditableMetadata(prev => ({ ...prev, title: nextValue }));
        }
      }
    } catch {}
    
    // Create deep copy to ensure proper state update
    setTemplateFieldValues(prev => {
      const newValues = { ...prev };
      
      if (
        processedValue === undefined
        || processedValue === null
        || processedValue === ''
        || (Array.isArray(processedValue) && processedValue.length === 0)
      ) {
        // Store an explicit empty value (instead of deleting the key) so the backend
        // clears the field. Clear both template keys (field.id and field.name) so the
        // save-time id/name sync can't restore the old value from the paired key.
        const emptyValue = Array.isArray(processedValue) ? [] : '';
        newValues[fieldId] = emptyValue;

        const templateField = (selectedTemplate?.fields || []).find(
          (f: any) => f && (f.id === fieldId || f.name === fieldId)
        );
        [templateField?.id, templateField?.name]
          .filter((key): key is string => Boolean(key) && key !== fieldId)
          .forEach((key) => {
            newValues[key] = emptyValue;
          });

        // Keep the mirrored 'gtin' property in sync when a GTIN/EAN field is cleared
        if (fieldId.toLowerCase().includes('gtin') || fieldId.toLowerCase().includes('ean')) {
          newValues['gtin'] = emptyValue;
        }
      } else {
        // Otherwise store the value
        newValues[fieldId] = processedValue;
        
        // Special handling for GTIN fields - also store value in 'gtin' field
        // This ensures that the GTIN value is always available in the 'gtin' field
        if (fieldId.toLowerCase().includes('gtin') || fieldId.toLowerCase().includes('ean')) {
          console.log(`Found GTIN-like field: ${fieldId}, storing value (${processedValue}) in 'gtin' property as well`);
          newValues['gtin'] = processedValue;
        }
      }
      
      console.log('New template field values:', newValues);
      return newValues;
    });
  };

  // Make sure the saving state exists
  const [saving, setSaving] = useState(false);

  // Update the handleSave function to use setSaving
  const handleSave = async () => {
    if (!asset) return;

    if (hasInvalidTemplateUrls) {
      setError('Korrigera ogiltiga URL-falt innan du sparar.');
      return;
    }

    setSaving(true); // Use setSaving instead of setLoading
    setSaveMessage(null);
    setError(null);

    try {
      // Ensure category IDs are valid MongoDB ObjectIds or convert them if necessary
      // For simplicity, assuming selectedCategoryIds are already valid strings that can be ObjectIds
      const validCategoryIds = selectedCategoryIds.filter(id => {
        // Basic check, ideally use a regex or a library for robust ObjectId validation
        return /^[0-9a-fA-F]{24}$/.test(id); 
      });

      // Clean template field values to ensure no special characters or internal properties
      const cleanMetadata: Record<string, any> = {};
      
      // Only keep simple values and arrays, avoid circular references or complex objects
      Object.entries(templateFieldValues).forEach(([key, value]) => {
        // Skip any keys that start with $ (MongoDB doesn't allow these)
        if (key.startsWith('$')) return;
        
        // Determine the type of value and handle accordingly
        if (value === null || value === undefined) {
          // Skip null/undefined values
          return;
        } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          // Simple values can be stored directly
          cleanMetadata[key] = value;
        } else if (value instanceof Date) {
          // Convert dates to ISO strings
          cleanMetadata[key] = value.toISOString();
        } else if (Array.isArray(value)) {
          // Arrays may contain structured objects for reference fields, so serialize them safely.
          cleanMetadata[key] = JSON.parse(JSON.stringify(
            value.map((item) => item instanceof Date ? item.toISOString() : item),
          ));
        } else if (typeof value === 'object') {
          try {
            // Try to convert to clean JSON and back to remove non-serializable parts
            const safeValue = JSON.parse(JSON.stringify(value));
            cleanMetadata[key] = safeValue;
          } catch (err) {
            console.warn(`Skipping complex field: ${key}`);
          }
        }
      });

      // Also include template "System ID" keys (field.name) so stable metadata keys stay in sync.
      // This is critical for VIN/Produkter where the app expects e.g. "Produktnamn_vin_id", "volym_vin_id", etc.
      try {
        if (selectedTemplate?.fields?.length) {
          selectedTemplate.fields.forEach((field: any) => {
            if (!field || field.type === 'separator') return;
            const idKey = String(field.id || '');
            const nameKey = String(field.name || '');
            if (!idKey || !nameKey) return;

            const rawVal = cleanMetadata[idKey] !== undefined ? cleanMetadata[idKey] : cleanMetadata[nameKey];
            if (rawVal === undefined) return;

            cleanMetadata[idKey] = rawVal;
            if (nameKey !== idKey) {
              cleanMetadata[nameKey] = rawVal;
            }
          });
        }
      } catch {}
      
      console.log('Sending cleaned metadata:', cleanMetadata);
      
      // Construct the update URL
      const updateUrl = `/api/assets/${asset._id}`;
      
      // If filnamn har ändrats, gör rename först
      const newFileName = (editableMetadata as any).fileName as string | undefined;
      if (newFileName && newFileName.trim() && newFileName !== asset.fileName) {
        await axiosInstance.put(`/api/assets/${asset._id}/rename`, { fileName: newFileName.trim() });
      }

      // Send all editable metadata, including updated categories and template fields
      const response = await axiosInstance.put(updateUrl, { // Changed to axiosInstance
        ...editableMetadata,
        categories: validCategoryIds, // Send validated category IDs
        metadata: cleanMetadata, // Send the cleaned template field values
        templateId: selectedTemplate?._id,
        aiDescription: editableAIDescription, // Send AI description
        aiTags: editableAITags // Send AI tags
      });

      setAsset(response.data);
      setSaveMessage('Ändringar sparade!');
      setIsEditing(false);
      if (onAssetUpdate) { // NY
        onAssetUpdate();
      }
    } catch (err: any) {
      console.error('Error saving asset:', err.response ? err.response.data : err.message);
      setError(err.response?.data?.message || err.message || 'Kunde inte spara ändringar.');
      setSaveMessage(null);
    } finally {
      setSaving(false); // Use setSaving instead of setLoading
      // Clear message after a few seconds
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleOpenRecipeCategoryDialog = () => {
    setSelectedRecipeSubCategoryId(currentRecipeSubCategoryId || recipeSubcategories[0]?._id || '');
    setRecipeCategoryDialogOpen(true);
  };

  const handleCloseRecipeCategoryDialog = () => {
    if (convertingToRecipe) return;
    setRecipeCategoryDialogOpen(false);
  };

  const handleConvertToRecipe = async () => {
    if (!asset || !recipeRootCategory) return;

    const nextCategoryIds = [
      recipeRootCategory._id,
      ...(selectedRecipeSubCategoryId ? [selectedRecipeSubCategoryId] : []),
    ];

    setConvertingToRecipe(true);
    setSyncMessage(null);

    try {
      const response = await axiosInstance.put(`/api/assets/${asset._id}`, {
        categories: nextCategoryIds,
      });

      setAsset(response.data);
      setSelectedCategoryIds(response.data?.categories || nextCategoryIds);
      setRecipeCategoryDialogOpen(false);
      setSyncMessage({
        type: 'success',
        text: 'Bilden har gjorts om till ett recept.',
        duration: 4000,
      });

      await fetchAssetDetails(asset._id);
      if (onAssetUpdate) {
        onAssetUpdate();
      }
    } catch (err: any) {
      setSyncMessage({
        type: 'error',
        text: err.response?.data?.message || err.message || 'Kunde inte göra bilden till ett recept.',
        duration: 6000,
      });
    } finally {
      setConvertingToRecipe(false);
    }
  };

  const loadActiveDownloadTemplates = useCallback(async () => {
    setLoadingDownloadTemplates(true);
    setDownloadTemplatesError(null);
    try {
      const data = await fetchActiveImageDownloadTemplates();
      setActiveDownloadTemplates(data);
    } catch (err: any) {
      setActiveDownloadTemplates([]);
      setDownloadTemplatesError(err?.message || 'Kunde inte ladda egna templates.');
    } finally {
      setLoadingDownloadTemplates(false);
    }
  }, []);

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
    loadActiveDownloadTemplates();
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleBuiltInDownload = async (templateId: BuiltInDownloadTemplateId) => {
    if (!asset) return;

    setSaveMessage('Laddar ner fil...');
    try {
      await downloadAssetsWithBuiltInTemplate([asset], templateId);
      setSaveMessage('Nedladdning slutförd!');
      handleDownloadMenuClose();
    } catch (err: any) {
      console.error('Error downloading file:', err);
      setSaveMessage(`Nedladdningsfel: ${err?.message || 'Okänt fel'}`);
    }
  };

  const handleCustomTemplateDownload = async (templateId: string) => {
    if (!asset) return;

    setSaveMessage('Laddar ner fil...');
    try {
      await runCustomAssetDownloadTemplate({
        templateId,
        assetIds: [asset._id],
        primaryAssetFileName: asset.fileName,
      });
      setSaveMessage('Nedladdning slutförd!');
      handleDownloadMenuClose();
    } catch (err: any) {
      console.error('Error downloading custom template:', err);
      setSaveMessage(`Nedladdningsfel: ${err?.response?.data?.message || err?.message || 'Okänt fel'}`);
    }
  };

  // Handle View Version
  const handleViewVersion = async (version: IAssetVersion) => {
      if (!assetId) return; // Should not happen
      setViewingVersion(version);
      setSaveMessage(`Viewing version ID: ${version.versionId.toString().substring(0, 6)}...`); // Optional message
  };

  // Handle Restore Version
  const handleRestoreVersion = async (version: IAssetVersion) => {
      if (!assetId) return; // Should not happen
      setVersionToRestore(version);
      setConfirmRestoreOpen(true);
  };

  // Handle Toggle Favorite
  const handleToggleFavorite = async () => {
      if (!assetId || !asset) return; // Should not happen if button is visible/enabled

      setTogglingFavorite(true);
      setSaveMessage(null);
      try {
          const newFavoriteStatus = !asset.isFavorite;
          const response = await axiosInstance.put(`/api/assets/${assetId}/favorite`, {
              isFavorite: newFavoriteStatus
          });
          // Update asset state with the new favorite status, keeping other fields
          setAsset(prevAsset => prevAsset ? { ...prevAsset, isFavorite: response.data.isFavorite } : null);
          setSaveMessage(newFavoriteStatus ? 'Marked as favorite!' : 'Removed from favorites.');
      } catch (err: any) {
          setSaveMessage(`Failed to toggle favorite status: ${err.message}`);
      } finally {
          setTogglingFavorite(false);
      }
  };

  // Handle Collection Toggle
  const handleCollectionToggle = async (collectionId: string, isCurrentlyMember: boolean) => {
      if (!assetId || !asset) return; // Should not happen

      setTogglingCollection(true);
      setSaveMessage(null);

      try {
          if (isCurrentlyMember) {
              // Remove asset from collection
              await axiosInstance.delete(`/api/collections/${collectionId}/assets/${assetId}`);
              setSaveMessage('Asset removed from collection.');
          } else {
              // Add asset to collection
              await axiosInstance.post(`/api/collections/${collectionId}/assets`, { assetId });
              setSaveMessage('Asset added to collection.');
          }
          // Refetch asset details to update the collections list for this asset
          fetchAssetDetails(assetId);

      } catch (err: any) {
          setSaveMessage(`Failed to update collection: ${err.message}`);
      } finally {
          setTogglingCollection(false);
      }
  };

  // Back to Current Version
  const handleBackToCurrentVersion = () => {
      setViewingVersion(null);
      setSaveMessage(null); // Clear message
  };

  // Helper to find category name by ID
  const getCategoryNameById = (categoryId: string): string => {
      const category = allCategories.find(cat => cat._id === categoryId);
      return category ? category.name : 'Okänd kategori';
  };

  // Helper function to decode filenames with special characters
  const decodeFileName = (fileName: string): string => {
    const repairMojibake = (value: string): string => {
      const mojibakePattern = /[\u00C0-\u00DF][\u0080-\u00BF]|Ã[¤¥¶Ä¥–]|Ã\x83|Â|Ã¤|Ã¶|Ã¥|Ã„|Ã–|Ã…/;
      if (!mojibakePattern.test(value)) return value;
      try {
        const bytes = Uint8Array.from(Array.from(value, ch => ch.charCodeAt(0) & 0xff));
        const fixed = new TextDecoder('utf-8').decode(bytes).normalize('NFC');
        if (fixed && fixed !== value && !fixed.includes('\uFFFD')) {
          return fixed;
        }
      } catch {}
      return value;
    };

    try {
      const decoded = decodeURIComponent(fileName.replace(/\+/g, ' '));
      return repairMojibake(decoded).normalize('NFC');
    } catch (e) {
      console.error('Error decoding filename:', e);
      return repairMojibake(fileName).normalize('NFC');
    }
  };

  // Determine which file to display based on viewingVersion state
  const fileToDisplay = viewingVersion ? { ...viewingVersion } : asset ? { ...asset, filePath: asset.filePath, fileName: asset.fileName, fileType: asset.fileType } : null;
  const previewPath = viewingVersion 
      ? null // No preview for older versions in this simple setup
      : (asset?.fileType?.startsWith('image/') ? asset?.previewPaths?.medium || asset?.previewPaths?.thumbnail : null);
  const isImage = asset?.fileType?.startsWith('image/');

  const deleteAssetDirectly = async () => {
    if (!assetId || !asset) return;

    setIsDeleting(true);
    setSaveMessage('Raderar asset...');
    try {
      await axiosInstance.delete(`/api/assets/${assetId}`);
      onAssetDeleted(assetId);
      setSaveMessage('Asset raderad framgångsrikt.');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Okänt fel';
      setSaveMessage(`Misslyckades att radera asset: ${message}`);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle Delete Asset
  const handleDeleteAsset = async () => {
      if (!assetId || !asset) return; // Should not happen

      const shouldUseRelationImpactDialog = (
        isAssetUnderNamedCategoryRoot(asset, 'recept')
        || isAssetUnderNamedCategoryRoot(asset, 'produkter')
      );

      let wordPressImpacts: WordPressArchiveUnpublishImpact[] = [];
      try {
        wordPressImpacts = await getWordPressArchiveUnpublishImpacts([assetId]);
      } catch (error: any) {
        setSaveMessage(error?.response?.data?.message || error?.message || 'Kunde inte kontrollera WordPress-publicering.');
        return;
      }

      if (shouldUseRelationImpactDialog || wordPressImpacts.length > 0) {
        setDeleteWordPressImpacts(wordPressImpacts);
        setRecipeDeleteImpactDialogOpen(true);
        return;
      }

      if (window.confirm(`Är du säker på att du vill radera asseten "${asset.fileName}"? Denna åtgärd kan inte ångras.`)) {
        try {
          await deleteAssetDirectly();
        } catch {}
      }
  };

  const handleDeleteWithWordPressUnpublish = async () => {
    await requestWordPressArchiveUnpublish(deleteWordPressImpacts);
    await deleteAssetDirectly();
  };

  // Add active collections filtering
  const getActiveCollections = () => {
    if (!asset || !asset.collections || asset.collections.length === 0) {
      return [];
    }
    
    return allCollections.filter(collection => 
      asset.collections.includes(collection._id)
    );
  };

  // Add this function before the return statement
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  // Funktion för att synkronisera med Dabas
  const handleSyncWithDabas = async () => {
    if (!asset || isDabasSyncing) return;
    
    try {
      setIsDabasSyncing(true);
      setSyncMessage({ type: 'info', text: 'Synkroniserar med Dabas...' });
      
      console.log(`Attempting to sync asset ${asset._id} with Dabas...`);
      const response = await axiosInstance.post(`/api/external/sync-asset/${asset._id}?system=dabas`);
      
      if (response.data.success) {
        setSyncMessage({
          type: 'success',
          text: `Synkronisering lyckades! ${response.data.syncedFields || 0} fält uppdaterades från Dabas.`,
          duration: 5000
        });
        
        // Uppdatera asset-data för att visa de nya värdena
        fetchAssetDetails(asset._id);
        
        // Om ett callback för uppdatering finns, anropa det
        if (onAssetUpdate) {
          onAssetUpdate();
        }
      } else {
        setSyncMessage({
          type: 'error',
          text: response.data.message || 'Synkronisering misslyckades',
          duration: 8000
        });
        console.error("Dabas sync failed with message:", response.data.message);
      }
    } catch (error: any) {
      console.error('Error syncing with Dabas:', error);
      
      let errorMessage = "Ett okänt fel uppstod";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSyncMessage({
        type: 'error',
        text: `Fel vid synkronisering med Dabas: ${errorMessage}`,
        duration: 10000
      });
    } finally {
      setIsDabasSyncing(false);
    }
  };

  // Funktion för att synkronisera med Validoo
  const handleSyncWithValidoo = async () => {
    if (!asset || isValidooSyncing) return;
    
    try {
      setIsValidooSyncing(true);
      setSyncMessage({ type: 'info', text: 'Synkroniserar med Validoo...' });
      
      console.log(`Attempting to sync asset ${asset._id} with Validoo...`);
      const response = await axiosInstance.post(`/api/external/sync-asset/${asset._id}?system=validoo`);
      
      if (response.data.success) {
        setSyncMessage({
          type: 'success',
          text: `Synkronisering lyckades: ${response.data.syncedFields} fält uppdaterades`,
          duration: 8000 
        });
        
        // Uppdatera asset-data för att visa de nya värdena
        fetchAssetDetails(asset._id);
        refreshValidooSyncStatus(asset._id);
        
        // Om ett callback för uppdatering finns, anropa det
        if (onAssetUpdate) {
          onAssetUpdate();
        }
      } else {
        setSyncMessage({
          type: 'error',
          text: `Synkronisering misslyckades: ${response.data.message}`,
          duration: 10000
        });
        console.error("Sync failed with message:", response.data.message);
      }
    } catch (error: any) {
      console.error('Error syncing with Validoo:', error);
      
      // Extract the specific error message from the API response
      let errorMessage = "Ett okänt fel uppstod";
      
      if (error.response) {
        // The server responded with an error status
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 404) {
          errorMessage = "Asset eller mall hittades inte";
        } else if (error.response.status === 400) {
          errorMessage = "Konfigurationsfel: Se till att en mall med Validoo-integration är konfigurerad";
        } else if (error.response.status === 401) {
          errorMessage = "Autentiseringsfel: Du måste vara inloggad";
        } else {
          errorMessage = `Server svarade med fel: ${error.response.status}`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = "Ingen respons från servern, kontrollera nätverksanslutningen";
      } else {
        // Something happened in setting up the request
        errorMessage = error.message || errorMessage;
      }
      
      setSyncMessage({
        type: 'error',
        text: `Fel vid synkronisering: ${errorMessage}`,
        duration: 10000
      });
    } finally {
      setIsValidooSyncing(false);
    }
  };

  // Function to sync all assets with Dabas
  const handleSyncAllWithDabas = async () => {
    setSyncAllProgressOpen(true);
    setSyncAllResults(null);
    
    try {
      setIsDabasSyncing(true);
      setSyncMessage({
        type: 'info',
        text: 'Synkroniserar alla produkter med Dabas... Detta kan ta en stund.',
        duration: 5000
      });
      
      const response = await axiosInstance.post('/api/external/sync-all-assets?system=dabas');
      
      if (response.data.success) {
        setSyncAllResults(response.data);
        setSyncMessage({
          type: 'success',
          text: `Synkronisering klar! ${response.data.successCount || 0} produkter synkades med Dabas.`,
          duration: 8000
        });
        
        // If current asset is displayed, refresh it
        if (asset) {
          fetchAssetDetails(asset._id);
        }
      } else {
        setSyncAllResults(response.data);
        setSyncMessage({
          type: 'error',
          text: response.data.message || 'Synkronisering misslyckades',
          duration: 8000
        });
      }
    } catch (error: any) {
      console.error('Error syncing all assets with Dabas:', error);
      
      let errorMessage = "Ett okänt fel uppstod";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSyncAllResults({
        success: false,
        message: errorMessage,
        totalAssets: 0,
        successCount: 0,
        failedCount: 0,
        failures: []
      });
      setSyncMessage({
        type: 'error',
        text: `Fel vid synkronisering med Dabas: ${errorMessage}`,
        duration: 10000
      });
    } finally {
      setIsDabasSyncing(false);
    }
  };

  // Function to sync all assets with Validoo
  const handleSyncAllWithValidoo = async () => {
    // Show progress dialog while request runs
    setSyncAllProgressOpen(true);
    setSyncAllResults(null);
    setShowSuccessList(false);
    try {
      setIsValidooSyncing(true);
      setSyncMessage({ 
        type: 'info', 
        text: 'Startar synkronisering av alla produkter med Validoo. Detta kan ta en stund...',
        duration: 5000
      });
      
      const response = await axiosInstance.post('/api/external/sync-all-assets?system=validoo');
      
      if (response.data.success) {
        setSyncAllResults(response.data);
        setSyncMessage({
          type: 'success',
          text: `${response.data.message}. Lyckades: ${response.data.successCount}, Misslyckades: ${response.data.failedCount}`,
          duration: 10000
        });
        
        // If current asset is displayed, refresh it
        if (asset) {
          fetchAssetDetails(asset._id);
          refreshValidooSyncStatus(asset._id);
        }
      } else {
        setSyncAllResults(response.data);
        setSyncMessage({
          type: 'error',
          text: `Kunde inte synkronisera produkter: ${response.data.message}`,
          duration: 10000
        });
      }
    } catch (error: any) {
      console.error('Error syncing all assets with Validoo:', error);
      
      let errorMessage = "Ett okänt fel uppstod";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSyncMessage({
        type: 'error',
        text: `Fel vid synkronisering: ${errorMessage}`,
        duration: 10000
      });
    } finally {
      setIsValidooSyncing(false);
      // Keep the dialog open to show results until the user hides it
    }
  };

  // Create a debug function to show metadata
  const handleDebugMetadata = () => {
    if (!asset || !asset.metadata) return;
    
    console.log("Asset Metadata:", asset.metadata);
    
    // Find keys that might contain GTIN
    const gtinKeys = Object.keys(asset.metadata || {}).filter(key => 
      key.toLowerCase().includes('gtin') || 
      key.toLowerCase().includes('ean') ||
      key.toLowerCase().includes('barcode')
    );
    
    console.log("Potential GTIN fields:", gtinKeys);
    if (gtinKeys.length > 0) {
      gtinKeys.forEach(key => {
        console.log(`Field '${key}' value:`, asset.metadata?.[key]);
      });
    }
    
    // Also log the raw 'gtin' field that the system is looking for
    console.log("Raw 'gtin' field exists:", asset.metadata && 'gtin' in asset.metadata);
    console.log("Raw 'gtin' value:", asset.metadata?.gtin);
    
    // Show in UI as alert
    alert(`Metadata debugging info has been logged to console.
Check the browser developer console (F12) for details.
    
Looking for fields with 'gtin' but found: ${gtinKeys.join(', ')}
Raw 'gtin' field exists: ${asset.metadata && 'gtin' in asset.metadata}
Raw 'gtin' value: ${asset.metadata?.gtin || 'undefined'}`);
  };

  // Funktion för att uppdatera metadata och se till att viktiga fält sparas korrekt
  const handleMetadataChange = (fieldId: string, value: any) => {
    if (!asset || !asset.metadata) return;
    
    const updatedMetadata = { ...asset.metadata };
    updatedMetadata[fieldId] = value;
    
    // Special handling for GTIN fields - also store value in lowercase 'gtin' property
    // Detta säkerställer att GTIN-värdet alltid finns i fältet 'gtin' som API:et söker efter
    if (fieldId.toLowerCase().includes('gtin') || fieldId.toLowerCase().includes('ean')) {
      console.log(`Found GTIN-like field: ${fieldId}, storing value in 'gtin' property as well`);
      updatedMetadata['gtin'] = value;
    }
    
    setAsset(prevAsset => prevAsset ? { ...prevAsset, metadata: updatedMetadata } : null);
  };

  // Drag and drop handlers for version upload
  const handleDragOver = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (readOnly) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      // Check if it's an image file (same type as current asset)
      if (file.type.startsWith(asset?.fileType?.split('/')[0] || '')) {
        // Store dropped file and open modal
        setDroppedFile(file);
        setIsVersionUploadOpen(true);
      } else {
        setSyncMessage({
          type: 'error',
          text: 'Filen måste vara av samma typ som nuvarande tillgång',
          duration: 3000
        });
      }
    }
  };

  if (!assetId) {
    return <Typography variant="h6">Select an asset to view details.</Typography>;
  }

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">Error loading asset details: {error}</Typography>;
  }

  if (!asset) {
      return <Typography variant="h6">Asset not found.</Typography>;
  }

  // Sort versions by creation date, latest first
  // Ensure asset.versions is treated as an array before sorting
  const sortedVersions = asset.versions && Array.isArray(asset.versions) 
    ? [...asset.versions].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) 
    : [];
  const canOpenFullPage = Boolean(onOpenFullPage && asset && layoutVariant !== 'fullPage');
  const isFullPageLayout = layoutVariant === 'fullPage';

  // These sections render in the main flow in panel mode, but move to the left
  // "about the file" rail in full page mode, hence the shared render helpers.
  const renderCategoriesSection = () => {
    if (!asset) return null;
    return (
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>Kategorier</Typography>

        {isEditing ? (
          // Show hierarchical checkboxes for editing with collapsible subcategories
          allCategories && allCategories.length > 0 ? (
            <List dense={true} sx={{ pl: 2 }}>
              {allCategories
                .filter(category => !category.parentCategory)
                .map(mainCategory => renderCategoryNode(mainCategory, 0))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">Inga kategorier tillgängliga.</Typography>
          )
        ) : (
          // Show both main categories and subcategories in view mode
          asset.categories && asset.categories.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(() => {
                // Create a map of categories by ID for easier lookup
                const categoryById: Record<string, ICategory> = {};
                allCategories.forEach(cat => {
                  categoryById[cat._id] = cat;
                });

                // For each assigned category, determine if it's a main or subcategory
                return asset.categories.map(categoryId => {
                  const category = categoryById[categoryId];
                  if (!category) return null;

                  // If it's a subcategory, show "MainCategory > SubCategory"
                  if (category.parentCategory) {
                    const parentId = typeof category.parentCategory === 'string'
                      ? category.parentCategory
                      : (category.parentCategory as any)?._id || '';

                    const parentCategory = categoryById[parentId];
                    const parentName = parentCategory ? parentCategory.name : '';

                    return (
                      <Chip
                        key={categoryId}
                        label={`${parentName} > ${category.name}`}
                        size="small"
                        sx={{
                          mb: 0.5,
                          bgcolor: '#f5f5f5'
                        }}
                      />
                    );
                  }
                  // If it's a main category, show it bold
                  else {
                    return (
                      <Chip
                        key={categoryId}
                        label={category.name}
                        size="small"
                        sx={{
                          mb: 0.5,
                          fontWeight: 'bold',
                          bgcolor: '#e0e0e0'
                        }}
                      />
                    );
                  }
                });
              })()}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">Inga kategorier tilldelade.</Typography>
          )
        )}
      </Paper>
    );
  };

  const renderCollectionsSection = () => {
    if (!asset) return null;
    if (!((!isEditing && getActiveCollections().length > 0) || isEditing)) return null;
    return (
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>Samlingar</Typography>

        {loadingCollections ? (
          <CircularProgress size={20} />
        ) : collectionError ? (
          <Typography color="error" variant="body2">Kunde inte ladda samlingar: {collectionError}</Typography>
        ) : allCollections && allCollections.length > 0 ? (
          <List dense={true}>
            {isEditing ? (
              // Show all collections in edit mode
              allCollections.map(collection => {
                const isMember = asset.collections && asset.collections.includes(collection._id);
                return (
                  <ListItem key={collection._id} sx={{ p: 0 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isMember}
                          onChange={() => handleCollectionToggle(collection._id, isMember)}
                          size="small"
                          disabled={togglingCollection}
                        />
                      }
                      label={collection.name}
                    />
                  </ListItem>
                );
              })
            ) : (
              // Show only collections that the asset is a member of
              getActiveCollections().map(collection => (
                <ListItem key={collection._id} sx={{ p: 0 }}>
                  <Chip
                    label={collection.name}
                    size="small"
                    sx={{ mb: 0.5, bgcolor: '#f5f5f5' }}
                  />
                </ListItem>
              ))
            )}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary">Inga samlingar tillgängliga.</Typography>
        )}
      </Paper>
    );
  };

  const renderDetailedMetadataSection = () => {
    if (!asset) return null;
    return (
      <>
        {onOpenRecipeCorrection && isAssetUnderNamedCategoryRoot(asset, 'recept') && (
          <RecipeCorrectionApprovalBar
            assetId={String(asset._id)}
            onViewChanges={() => onOpenRecipeCorrection(String(asset._id), { snapshot: true })}
          />
        )}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Filtyp:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem' }}>{asset.fileType}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Asset ID:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem', fontFamily: 'monospace' }}>{asset._id}</Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Uppladdad:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem' }}>{new Date(asset.uploadedAt).toLocaleString()}</Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Publicerad i WordPress:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
              {asset.wordpressPublishedAt ? new Date(asset.wordpressPublishedAt).toLocaleString() : '—'}
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Senast modifierad:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem' }}>{new Date(asset.modifiedAt).toLocaleString()}</Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Qbank ID:</Typography>
            {isEditing ? (
              <TextField
                size="small"
                name="qbankId"
                value={editableMetadata.qbankId ?? ''}
                onChange={handleEditChange}
                fullWidth
                variant="outlined"
                sx={{ mb: 0.5, '& .MuiInputBase-input': { fontSize: '0.7rem', fontFamily: 'monospace' } }}
              />
            ) : (
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem', fontFamily: 'monospace' }}>{asset.qbankId || '—'}</Typography>
            )}

            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Qbank Creation Date:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
              {asset.qbankCreationDate ? new Date(asset.qbankCreationDate).toLocaleString() : '—'}
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Uppladdad av:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.7rem' }}>{uploaderLabel || '—'}</Typography>
          </Box>
        </Box>
      </Paper>
      </>
    );
  };

  const renderFullPageDescriptionSection = () => {
    if (!asset) return null;
    return (
      <Paper elevation={0} sx={{ p: 0, mb: 2, border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            background: '#f8f9fa',
            borderBottom: '1px solid #e2e8f0',
            minHeight: '32px',
            '& .MuiTab-root': {
              fontSize: '0.78rem',
              minHeight: '32px',
              padding: '2px 8px !important',
              textTransform: 'none',
            },
          }}
        >
          <Tab label="AI Beskrivning" />
          <Tab label="Beskrivning & Taggar" />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ p: 1.5 }}>
            {isEditing ? (
              <TextField
                name="aiDescription"
                value={editableAIDescription}
                onChange={(e) => setEditableAIDescription(e.target.value)}
                fullWidth
                multiline
                rows={4}
                size="small"
                placeholder="AI-genererad beskrivning"
                sx={{ mb: 1.5 }}
              />
            ) : (
              <Typography sx={{ color: '#2d3748', fontSize: '0.82rem', lineHeight: 1.35, mb: 1.5 }}>
                {asset.aiDescription || 'Ingen AI-beskrivning tillgänglig.'}
              </Typography>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              AI Taggar
            </Typography>
            {isEditing ? (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Lägg till AI-tagg, separera med komma"
                  value={editableAITags.join(', ')}
                  onChange={(e) => setEditableAITags(e.target.value.split(',').map(tag => tag.trim()).filter(Boolean))}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {editableAITags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" onDelete={() => handleRemoveAITag(tag)} />
                  ))}
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {asset.aiTags && asset.aiTags.length > 0 ? (
                  asset.aiTags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" variant="outlined" sx={{ bgcolor: '#f0f7ff' }} />
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">Inga AI-taggar tillgängliga.</Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Beskrivning</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                name="description"
                value={editableMetadata.description || ''}
                onChange={handleEditChange}
                size="small"
                sx={{ mb: 1.5 }}
              />
            ) : (
              <Typography sx={{ color: '#2d3748', fontSize: '0.82rem', lineHeight: 1.35, mb: 1.5 }}>
                {asset.description || 'Ingen beskrivning'}
              </Typography>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Taggar</Typography>
            {isEditing ? (
              <Box>
                <TextField
                  fullWidth
                  placeholder="Skriv tag och tryck Enter"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyDown={handleTagInputKeyDown}
                  size="small"
                  sx={{ mb: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            if (tagInput.trim()) {
                              if (!editableMetadata.tags?.includes(tagInput.trim())) {
                                setEditableMetadata(prev => ({
                                  ...prev,
                                  tags: [...(prev.tags || []), tagInput.trim()]
                                }));
                              }
                              setTagInput('');
                            }
                          }}
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {editableMetadata.tags && editableMetadata.tags.length > 0 ? (
                    editableMetadata.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" onDelete={() => handleRemoveTag(tag)} sx={{ mb: 0.5, bgcolor: '#f5f5f5' }} />
                    ))
                  ) : (
                    <Typography variant="body2">Inga taggar tillagda</Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {asset.tags && asset.tags.length > 0 ? (
                  asset.tags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" sx={{ mb: 0.5, bgcolor: '#f5f5f5' }} />
                  ))
                ) : (
                  <Typography variant="body2">Inga taggar</Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const renderFullPageHistorySection = () => {
    if (!asset) return null;
    const previousVersions = (asset.versions || [])
      .filter((version) => version.versionId.toString() !== asset._id)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return (
      <Paper elevation={0} sx={{ p: 0, mb: 2, border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
        <Tabs
          value={historyActiveTab}
          onChange={(event, newValue) => setHistoryActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            background: '#f8f9fa',
            borderBottom: '1px solid #e2e8f0',
            minHeight: '32px',
            '& .MuiTab-root': {
              fontSize: '0.78rem',
              minHeight: '32px',
              padding: '2px 8px !important',
              textTransform: 'none',
            },
          }}
        >
          <Tab label="Versioner" />
          <Tab label="Metadata" />
          <Tab label="Historik" />
        </Tabs>

        {historyActiveTab === 0 && (
          <Box sx={{ p: 1.5 }}>
            <List dense>
              <ListItem sx={{ px: 0, alignItems: 'flex-start', gap: 1 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  <VersionThumbnailImage
                    src={currentVersionThumbnailSrc}
                    fallbackSrc={currentVersionFallbackSrc}
                    alt="Current version thumbnail"
                    placeholderLabel="CURRENT"
                  />
                </Box>
                <ListItemText
                  primary={`Version ${previousVersions.length + 1}`}
                  secondary={`${new Date(asset.uploadedAt).toLocaleString('sv-SE')} · Aktuell version`}
                  primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
                <Chip label="Aktuell" size="small" color="success" />
              </ListItem>
              {previousVersions.map((version, index) => (
                <ListItem
                  key={version.versionId}
                  sx={{ px: 0, alignItems: 'flex-start', gap: 1 }}
                  secondaryAction={readOnly ? undefined : (
                    <IconButton edge="end" aria-label="restore" onClick={() => handleRestoreVersion(version)} disabled={restoring}>
                      {restoring ? <CircularProgress size={18} /> : <RestoreIcon />}
                    </IconButton>
                  )}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    <VersionThumbnailImage
                      src={
                        buildStaticThumbnailUrl(version.thumbnailPath, version.createdAt || version.versionId)
                        || (canUseVersionThumbnailEndpoint(version.filePath)
                          ? buildVersionThumbnailUrl(asset._id, version.versionId, version.createdAt || version.versionId)
                          : null)
                      }
                      fallbackSrc={
                        version.thumbnailPath && canUseVersionThumbnailEndpoint(version.filePath)
                          ? buildVersionThumbnailUrl(asset._id, version.versionId, version.createdAt || version.versionId)
                          : null
                      }
                      alt={`Version ${index + 1} thumbnail`}
                      placeholderLabel={`V${index + 1}`}
                    />
                  </Box>
                  <ListItemText
                    primary={`Version ${previousVersions.length - index}`}
                    secondary={new Date(version.createdAt).toLocaleString('sv-SE')}
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {historyActiveTab === 1 && (
          <Box sx={{ p: 1.5 }}>
            {loadingExif && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Läser metadata...</Typography>
              </Box>
            )}
            {!loadingExif && !exifData && (
              <Button
                variant="outlined"
                size="small"
                onClick={async () => {
                  if (!asset?._id) return;
                  setLoadingExif(true);
                  try {
                    const resp = await axiosInstance.get(`/api/assets/${asset._id}/metadata`);
                    setExifData(resp.data);
                  } catch (e: any) {
                    alert(e?.response?.data?.message || 'Kunde inte läsa metadata');
                  } finally {
                    setLoadingExif(false);
                  }
                }}
              >
                Läs metadata
              </Button>
            )}
            {!loadingExif && exifData && (
              <Box sx={{ display: 'grid', gap: 1 }}>
                {[
                  ['EXIF', Object.keys(exifData.exif || {}).length],
                  ['IPTC', Object.keys(exifData.iptc || {}).length],
                  ['XMP', Object.keys(exifData.xmp || {}).length],
                ].map(([label, count]) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="body2" color="text.secondary">{count} fält</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {historyActiveTab === 2 && (
          <Box sx={{ p: 1.5 }}>
            <AssetHistory assetId={asset._id} />
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box className="asset-detail-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : !asset ? (
        <Box sx={{ p: 2 }}>
          <Typography>Ingen tillgång vald</Typography>
        </Box>
      ) : viewingVersion ? (
        // Version View
        <Box sx={{ height: '100%', p: 2 }}>
          <Typography variant="h6">Viewing Version from {new Date(viewingVersion.createdAt).toLocaleString()}</Typography>
          {/* Add version preview here */}
          <Box sx={{ textAlign: 'center', my: 3 }}>
            {viewingVersion.previewUrl && (
              <img 
                src={viewingVersion.previewUrl} 
                alt="Version preview" 
                style={{ maxWidth: '100%', maxHeight: '400px' }} 
              />
            )}
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleBackToCurrentVersion}
              startIcon={<HistoryIcon />}
            >
              Back to Current Version
            </Button>
            {!readOnly && (
            <Button 
              variant="contained" 
              color="secondary" 
              onClick={() => handleRestoreVersion(viewingVersion)}
              startIcon={<RestoreIcon />}
              disabled={restoring}
            >
              {restoring ? <CircularProgress size={24} /> : 'Restore This Version'}
            </Button>
            )}
          </Box>
        </Box>
      ) : (
        // Normal Asset View/Edit - Fixed header with scrollable content below
        <>
          {/* Header - fixed at top */}
          <Box 
            sx={{ 
              bgcolor: '#f5f5f5',
              pt: 2,
              pb: 1,
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              borderBottom: '2px solid #e0e0e0',
              width: '100%',
              flexShrink: 0
            }}
          >
            {/* Title */}
            <Box>
              {isEditing ? (
                <TextField
                  name="title"
                  label="Titel"
                  value={editableMetadata.title || ''}
                  onChange={handleEditChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1, width: '100%', maxWidth: '400px' }}
                />
              ) : (
                <Typography 
                  variant="h5" 
                  component="h1" 
                  sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}
                >
                  {getAutoTitleForAsset(asset)}
                </Typography>
              )}
            </Box>
            
            {/* Action buttons row aligned left */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
              {/* Save and Cancel buttons when editing - displayed at the top */}
              {isEditing && (
                <>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={() => setIsEditing(false)}
                    size="medium"
                    sx={{ mr: 1 }}
                  >
                    Avbryt
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={handleSave}
                    disabled={saving || hasInvalidTemplateUrls}
                    size="medium"
                    sx={{ mr: 2 }}
                  >
                    {saving ? <CircularProgress size={20} /> : 'Spara ändringar'}
                  </Button>
                  
                  <Divider orientation="vertical" flexItem sx={{ mr: 2 }} />
                </>
              )}
            
              {/* Favorite Button */}
              {canFavorite && (
                <Tooltip title={asset.isFavorite ? "Ta bort från favoriter" : "Lägg till i favoriter"}>
                  <IconButton
                    color={asset.isFavorite ? "primary" : "default"}
                    onClick={handleToggleFavorite}
                    disabled={togglingFavorite}
                    size="medium"
                  >
                    {asset.isFavorite ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>
                </Tooltip>
              )}

              {/* Edit Button - show when not in edit mode */}
              {!isEditing && canUpdate && !readOnly && (
                <Tooltip title="Redigera">
                  <IconButton
                    color="primary"
                    onClick={() => setIsEditing(true)}
                    size="medium"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}

              {canOpenFullPage && asset && (
                <Tooltip title="Öppna stor vy">
                  <IconButton
                    color="primary"
                    onClick={() => onOpenFullPage?.(asset._id)}
                    size="medium"
                    aria-label="Öppna stor vy"
                  >
                    <OpenInFullIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* Download Button */}
              {canDownload && (
                <>
                  <Tooltip title="Ladda ner">
                    <IconButton
                      color="primary"
                      onClick={handleDownloadMenuOpen}
                      disabled={restoring}
                      size="medium"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={downloadMenuAnchor}
                    open={Boolean(downloadMenuAnchor)}
                    onClose={handleDownloadMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {asset && getAvailableBuiltInDownloadTemplates(asset).map((template) => (
                      <MenuItem
                        key={`detail-download-${template.id}`}
                        onClick={() => handleBuiltInDownload(template.id)}
                      >
                        <ListItemIcon>
                          {template.icon}
                        </ListItemIcon>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                          <ListItemText
                            primary={template.name}
                            sx={{ my: 0, minWidth: 0 }}
                          />
                          <Tooltip title={template.tooltip} placement="left" arrow>
                            <IconButton
                              size="small"
                              aria-label={`Information om ${template.name}`}
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              sx={{ p: 0.25, color: 'text.secondary' }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </MenuItem>
                    ))}
                    {loadingDownloadTemplates && (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Laddar egna templates...
                      </MenuItem>
                    )}
                    {downloadTemplatesError && (
                      <MenuItem disabled>
                        {downloadTemplatesError}
                      </MenuItem>
                    )}
                    {activeDownloadTemplates.length > 0 && (
                      <Divider />
                    )}
                    {activeDownloadTemplates.length > 0 && (
                      <MenuItem disabled>
                        Egna templates
                      </MenuItem>
                    )}
                    {activeDownloadTemplates.map((template) => (
                      <MenuItem
                        key={`detail-custom-download-${template.id}`}
                        onClick={() => handleCustomTemplateDownload(template.id)}
                      >
                        <ListItemIcon>
                          <DownloadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={template.name} />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}

              {/* PDF Download Button */}
              {canDownload && !readOnly && (
                <>
                  <Tooltip title="Skapa produktblad (PDF)">
                    <IconButton
                      color="primary"
                      onClick={handlePdfMenuOpen}
                      disabled={restoring || downloadingPdf}
                      size="medium"
                    >
                      {downloadingPdf ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={pdfMenuAnchor}
                    open={Boolean(pdfMenuAnchor)}
                    onClose={handlePdfMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {loadingPdfTemplates ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Laddar mallar...
                      </MenuItem>
                    ) : visiblePdfTemplates.length === 0 ? (
                      <MenuItem disabled>
                        Inga PDF-mallar tillgängliga
                      </MenuItem>
                    ) : (
                      visiblePdfTemplates.map(template => (
                        <MenuItem 
                          key={template._id} 
                          onClick={() => handlePdfDownload(template._id!)}
                        >
                          <ListItemIcon>
                            <PictureAsPdfIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={template.name}
                            secondary={[
                              template.type === 'products' ? 'Produkter' : 'Vin',
                              template.description || '',
                            ].filter(Boolean).join(' • ')}
                          />
                        </MenuItem>
                      ))
                    )}
                  </Menu>
                </>
              )}

              {/* Delete Button */}
              {canDelete && (
                <Tooltip title="Radera">
                  <IconButton
                    color="error"
                    onClick={handleDeleteAsset}
                    disabled={isDeleting}
                    size="medium"
                  >
                    {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                  </IconButton>
                </Tooltip>
              )}

              {/* Upload New Version button */}
              {canManageVersions && (
                <Tooltip title="Ladda upp en ny version av denna tillgång">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => setIsVersionUploadOpen(true)}
                      size="medium"
                    >
                      <UploadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {canManageVersions && onOpenAiImageStudioForRecipeAsset && asset.fileType?.startsWith('image/') && (
                <Tooltip title="Skapa ny receptbild med Athena AI Studio">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => onOpenAiImageStudioForRecipeAsset(String(asset._id))}
                      size="medium"
                    >
                      <AutoAwesomeIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {onOpenAiImageStudioForEditAsset && asset.fileType?.startsWith('image/') && (
                <Tooltip title="Redigera bild i Athena AI Studio">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => onOpenAiImageStudioForEditAsset(String(asset._id))}
                      size="medium"
                    >
                      <AutoFixHighIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {onOpenRecipeCorrection && isAssetUnderNamedCategoryRoot(asset, 'recept') && (
                <Tooltip title="Korrigera recept">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => onOpenRecipeCorrection(String(asset._id))}
                      size="medium"
                      aria-label="Korrigera recept"
                    >
                      <SpellcheckIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {canUpdate && isJpgOrPngAsset(asset) && (
                <Tooltip title={recipeRootCategory ? 'Gör till ett recept' : 'Kategorin Recept saknas'}>
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleOpenRecipeCategoryDialog}
                      disabled={!recipeRootCategory || convertingToRecipe}
                      size="medium"
                    >
                      {convertingToRecipe ? <CircularProgress size={20} /> : <RestaurantMenuIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Stock status (store/beställningsbara assets only) */}
              {isStoreAsset(asset) && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                  {loadingStock ? (
                    <CircularProgress size={18} />
                  ) : (
                    <>
                      <Chip
                        label={`${stockLevel || 0} st i lager`}
                        color={stockLevel === 0 ? 'error' : stockLevel && stockLevel <= 5 ? 'warning' : 'success'}
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                      />
                      {stockLevel === 0 && (
                        <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem', ml: 1 }}>
                          Slut i lager
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {/* Validoo sync button - HIDDEN: Dabas is now the primary sync source
              {canValidooSync && hasValidooIntegration && (
                <Tooltip title={asset.skipValidooSync ? "Inaktiverad: Produkten är markerad att inte synka med Validoo" : "Synkronisera denna produkt med Validoo för att hämta uppdaterade produktdata"}>
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleSyncWithValidoo}
                      disabled={isValidooSyncing || !!asset.skipValidooSync}
                      size="medium"
                    >
                      {isValidooSyncing ? <CircularProgress size={20} /> : <SyncIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              */}

              {/* Dabas sync button */}
              {canValidooSync && hasDabasIntegration && (
                <Tooltip title={asset.skipDabasSync ? "Inaktiverad: Produkten är markerad att inte synka med Dabas" : "Synkronisera denna produkt med Dabas för att hämta uppdaterade produktdata"}>
                  <span>
                    <IconButton
                      onClick={handleSyncWithDabas}
                      disabled={isDabasSyncing || !!asset.skipDabasSync}
                      size="medium"
                      sx={{ 
                        ml: 0.5,
                        color: '#ff9800',
                        '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.1)' }
                      }}
                    >
                      {isDabasSyncing ? <CircularProgress size={20} sx={{ color: '#ff9800' }} /> : <SyncIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Dabas sync status icon - green checkmark for success, orange X for previously synced but now failed, red X for never synced */}
              {hasDabasIntegration && asset.dabasSyncStatus === 'success' && !asset.skipDabasSync && (
                <Tooltip title={`Synkad med Dabas${asset.lastDabasSyncAt ? ` (${new Date(asset.lastDabasSyncAt).toLocaleString('sv-SE')})` : ''}`}>
                  <span>
                    <CheckCircleIcon sx={{ color: '#4caf50', ml: 0.5 }} />
                  </span>
                </Tooltip>
              )}
              {hasDabasIntegration && asset.dabasSyncStatus === 'failed' && !asset.skipDabasSync && !asset.hasPreviousDabasSync && (
                <Tooltip title={`Dabas-synk misslyckades${asset.lastDabasSyncError ? `: ${asset.lastDabasSyncError}` : ''}${asset.lastDabasSyncAt ? ` (${new Date(asset.lastDabasSyncAt).toLocaleString('sv-SE')})` : ''}`}>
                  <span>
                    <CancelIcon sx={{ color: '#f44336', ml: 0.5 }} />
                  </span>
                </Tooltip>
              )}
              {hasDabasIntegration && asset.dabasSyncStatus === 'failed' && !asset.skipDabasSync && asset.hasPreviousDabasSync && (
                <Tooltip title={`Tidigare synkad med Dabas men senaste synk misslyckades${asset.lastDabasSyncError ? `: ${asset.lastDabasSyncError}` : ''}${asset.lastDabasSyncAt ? ` (${new Date(asset.lastDabasSyncAt).toLocaleString('sv-SE')})` : ''}`}>
                  <span>
                    <CancelIcon sx={{ color: '#ff9800', ml: 0.5 }} />
                  </span>
                </Tooltip>
              )}

              {/* Allow asset updaters (including admins) to exclude an asset from Dabas sync */}
              {(canUpdate || isAdmin) && !readOnly && hasDabasIntegration && (
                <Tooltip title={asset.skipDabasSync ? 'Synka inte med Dabas (aktiverad)' : 'Synka inte med Dabas'}>
                  <span>
                    <Checkbox
                      checked={!!asset.skipDabasSync}
                      onChange={(e) => handleToggleSkipDabasSync(e.target.checked)}
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckBoxIcon />}
                      sx={{ p: 0, ml: 0.5 }}
                    />
                  </span>
                </Tooltip>
              )}

              {/* Validoo sync status - HIDDEN: Dabas is now the primary sync source
              {hasValidooIntegration && isValidooSynced && (
                <Tooltip title="Synkad med Validoo">
                  <span>
                    <CheckCircleIcon sx={{ color: '#4caf50', ml: 0.5 }} />
                  </span>
                </Tooltip>
              )}
              */}

              {/* Admin-only: icon-only toggle to skip Validoo sync - HIDDEN
              {isAdmin && hasValidooIntegration && (
                <Tooltip title={asset.skipValidooSync ? 'Ej synkad med Validoo' : 'Synka inte med Validoo'}>
                  <span>
                    <Checkbox
                      checked={!!asset.skipValidooSync}
                      onChange={(e) => handleToggleSkipValidooSync(e.target.checked)}
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckBoxIcon />}
                      sx={{ p: 0, ml: 0.5 }}
                    />
                  </span>
                </Tooltip>
              )}
              */}

              {/* Admin users can sync all assets with Validoo - HIDDEN
              {isAdmin && hasValidooIntegration && (
                <Tooltip title="Synkronisera alla produkter med Validoo. Detta kan ta en stund.">
                  <span>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={handleSyncAllWithValidoo}
                      disabled={isValidooSyncing}
                      startIcon={<CloudSyncIcon />}
                    >
                      {isValidooSyncing ? 'Synkroniserar...' : 'Synka alla (Validoo)'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              */}

            </Box>
          </Box>

          {/* Scrollable content area below fixed header */}
          <Box sx={{ 
            flexGrow: 1, 
            overflowY: 'auto', 
            p: 2 
          }}>
            {/* Confirm sync-all dialog */}
            <Dialog open={confirmSyncAllOpen} onClose={() => setConfirmSyncAllOpen(false)}>
              <DialogTitle>Synkronisera alla produkter?</DialogTitle>
              <DialogContent>
                <Typography>Är du säker att du vill synka alla produkter mot Validoo? Detta kan ta en stund.</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmSyncAllOpen(false)}>Nej</Button>
                <Button onClick={() => { setConfirmSyncAllOpen(false); handleSyncAllWithValidoo(); }} variant="contained" color="primary">Ja</Button>
              </DialogActions>
            </Dialog>

            {/* Progress dialog while sync-all is running */}
            <Dialog open={syncAllProgressOpen} onClose={() => setSyncAllProgressOpen(false)}>
              <DialogTitle>
                {isSyncing ? 'Synkroniserar alla produkter…' : 'Resultat av Validoo‑synk'}
              </DialogTitle>
              <DialogContent dividers>
                {isSyncing ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                    <LinearProgress sx={{ minWidth: 240 }} />
                    <Typography variant="body2">Arbetar…</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {syncAllResults && (
                      <>
                        <Typography variant="body2">{syncAllResults.message}</Typography>
                        <Typography variant="body2">Totalt: {syncAllResults.totalAssets} • Lyckades: {syncAllResults.successCount} • Misslyckades: {syncAllResults.failedCount}</Typography>
                        {syncAllResults.failedCount > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">Misslyckade</Typography>
                            <Box sx={{ maxHeight: 220, overflowY: 'auto', pr: 1 }}>
                              {syncAllResults.failures.map((f: any, idx: number) => (
                                <Typography key={idx} variant="body2" color="error">{f.assetId}: {f.message}</Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {!!syncAllResults.successAssetIds?.length && (
                          <Box sx={{ mt: 1 }}>
                            <Button size="small" onClick={() => setShowSuccessList(s => !s)}>
                              {showSuccessList ? 'Dölj lyckade' : `Visa lyckade (${syncAllResults.successAssetIds.length})`}
                            </Button>
                            <Collapse in={showSuccessList}>
                              <Box sx={{ maxHeight: 220, overflowY: 'auto', pr: 1 }}>
                                {syncAllResults.successAssetIds.map((id: string, idx: number) => (
                                  <Typography key={idx} variant="body2">OK: {id}</Typography>
                                ))}
                              </Box>
                            </Collapse>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                {syncAllResults && (
                  <Button
                    onClick={async () => {
                      try {
                        const lines: string[] = [];
                        if (syncAllResults?.successAssetIds?.length) {
                          syncAllResults.successAssetIds.forEach((id: string) => lines.push(`OK ${id}`));
                        }
                        if (syncAllResults?.failures?.length) {
                          syncAllResults.failures.forEach((f: any) => lines.push(`ERROR ${f.assetId}: ${f.message}`));
                        }
                        await navigator.clipboard.writeText(lines.join('\n'));
                      } catch {}
                    }}
                    size="small"
                  >
                    Kopiera logg
                  </Button>
                )}
                <Button onClick={() => setSyncAllProgressOpen(false)}>Dölj</Button>
              </DialogActions>
            </Dialog>
            {/* Full page: two-zone layout with an "about the file" left rail. Panel: wrappers are inert (display: contents). */}
            <Box
              sx={isFullPageLayout ? {
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'minmax(320px, 1fr) minmax(0, 2fr)',
                  xl: 'minmax(0, 1fr) minmax(0, 3fr)',
                },
                gap: 2,
                alignItems: 'start',
              } : { display: 'contents' }}
            >
            <Box sx={isFullPageLayout ? { minWidth: 0 } : { display: 'contents' }}>
            {!readOnly && asset && (getAssetPdfType(asset) !== null || isAssetUnderNamedCategoryRoot(asset, 'recept')) && (
              <AssetWordPressPanel assetId={asset._id} />
            )}
            {/* Preview Area with Drag & Drop */}
            <Box 
              sx={{ 
                mb: 3, 
                textAlign: 'center', 
                bgcolor: isDragOver ? '#e3f2fd' : 'white', 
                p: 2, 
                borderRadius: '4px',
                border: isDragOver ? '2px dashed #1976d2' : '1px solid #e0e0e0',
                position: 'relative',
                transition: 'all 0.2s ease'
              }}
              onDragOver={readOnly ? undefined : handleDragOver}
              onDragLeave={readOnly ? undefined : handleDragLeave}
              onDrop={readOnly ? undefined : handleDrop}
            >
              {!readOnly && isDragOver && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  zIndex: 1
                }}>
                  <Typography variant="h6" color="primary">
                    Släpp för att ladda upp ny version
                  </Typography>
                </Box>
              )}
              
              {asset.fileType.startsWith('video/') ? (
                <VideoPlayer
                  src={(asset.filePath ? `${API_BASE_URL}/uploads/${(asset.filePath.split(/[/\\]/).pop() || '')}` : '')}
                  poster={asset.previewPaths?.medium ? `${API_BASE_URL}${asset.previewPaths.medium}` : undefined}
                  width="100%"
                  height="auto"
                  controls={true}
                  muted={false}
                />
              ) : previewImageUrl ? (
                <Box
                  component="img"
                  src={previewImageUrl}
                  alt={asset.fileName}
                  onClick={openFullscreen}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    boxShadow: 'none',
                    cursor: 'zoom-in',
                    opacity: isDragOver ? 0.5 : 1,
                    transition: 'opacity 0.2s ease'
                  }}
                />
              ) : isImage ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Laddar förhandsgranskning...</Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">Preview not available for this file type.</Typography>
              )}
              
              {!readOnly && !isDragOver && (
                <Typography 
                  variant="caption" 
                  color="textSecondary" 
                  sx={{ 
                    display: 'block', 
                    mt: 1, 
                    opacity: 0.7,
                    fontStyle: 'italic'
                  }}
                >
                  Dra och släpp en ny fil här för att ladda upp en ny version
                </Typography>
              )}
            </Box>

            {/* Fullscreen image dialog with navigation */}
            <Dialog 
              open={fullscreenOpen} 
              onClose={() => setFullscreenOpen(false)} 
              fullScreen
              BackdropProps={{
                sx: { backgroundColor: '#000' }
              }}
              PaperProps={{ 
                sx: { 
                  bgcolor: '#000',
                  cursor: 'pointer'
                } 
              }}
              onClick={() => setFullscreenOpen(false)}
            >
              {/* Close button */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setFullscreenOpen(false);
                }}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  zIndex: 10,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Previous button */}
              {hasPrevious && assetIds.length > 1 && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToPrevious();
                  }}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    zIndex: 10,
                    width: 48,
                    height: 48,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 32 }} />
                </IconButton>
              )}

              {/* Next button */}
              {hasNext && assetIds.length > 1 && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateToNext();
                  }}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    zIndex: 10,
                    width: 48,
                    height: 48,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 32 }} />
                </IconButton>
              )}

              {/* Position indicator */}
              {assetIds.length > 1 && currentIndex >= 0 && (
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    position: 'absolute',
                    bottom: 24,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    px: 2,
                    py: 0.5,
                    borderRadius: '16px',
                    fontSize: '0.875rem',
                    zIndex: 10
                  }}
                >
                  {currentIndex + 1} / {assetIds.length}
                </Box>
              )}
              
              <DialogContent 
                sx={{ 
                  p: 0, 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  overflow: 'hidden',
                  bgcolor: '#000',
                  position: 'relative',
                  minHeight: '100vh'
                }}
              >
                {/* Black overlay to guarantee background */}
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#000' }} />
                
                {fullscreenLoading && (
                  <CircularProgress 
                    sx={{ 
                      color: 'white', 
                      position: 'absolute', 
                      zIndex: 10 
                    }} 
                  />
                )}
                
                {displayedFullscreenImageUrl ? (
                  <Box
                    component="img"
                    src={displayedFullscreenImageUrl}
                    alt={asset?.fileName || 'Image'}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ 
                      maxWidth: '85vw', 
                      maxHeight: '90vh', 
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                      cursor: 'default',
                      opacity: fullscreenLoading ? 0.5 : 1,
                      transition: 'opacity 0.15s ease',
                      zIndex: 5
                    }}
                  />
                ) : previewImageUrl ? (
                  <Box
                    component="img"
                    src={previewImageUrl}
                    alt={asset?.fileName || 'Preview'}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ 
                      maxWidth: '85vw', 
                      maxHeight: '90vh', 
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                      cursor: 'default',
                      opacity: 0.4,
                      zIndex: 5
                    }}
                  />
                ) : null}
              </DialogContent>
            </Dialog>

            {/* Basic File Info - Top compact row */}
            <Paper elevation={0} sx={{ p: 1, mb: 2, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1, alignItems: 'center' }}>
                <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Filnamn</Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editableMetadata.fileName ?? decodeFileName(asset.fileName)}
                      onChange={(e) => setEditableMetadata(prev => ({ ...prev, fileName: e.target.value }))}
                      fullWidth
                      placeholder="Ange nytt filnamn (inkl. eller utan ändelse)"
                      inputProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  ) : readOnly && (asset as { originalFileName?: string }).originalFileName
                    && decodeFileName((asset as { originalFileName?: string }).originalFileName || '') !== decodeFileName(asset.fileName) ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '0.65rem' }}>{decodeFileName(asset.fileName)}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.7rem', mt: 0.5 }}>Ursprungligt filnamn</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.65rem' }}>
                        {decodeFileName((asset as { originalFileName?: string }).originalFileName || '')}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: '0.65rem' }}>{decodeFileName(asset.fileName)}</Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Filstorlek</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.65rem' }}>{formatFileSize(asset.fileSize)}</Typography>
                </Box>
                <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Dimensioner</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.65rem' }}>
                    {asset.width && asset.height ? `${asset.width}x${asset.height}` : '—'}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Full page left rail: categories, collections and file metadata live with the file info */}
            {isFullPageLayout && renderCategoriesSection()}
            {isFullPageLayout && renderCollectionsSection()}
            {isFullPageLayout && renderDetailedMetadataSection()}
            {isFullPageLayout && renderFullPageDescriptionSection()}
            {isFullPageLayout && renderFullPageHistorySection()}
            {isFullPageLayout && isAdmin && <VisualAssetProfilePanel assetId={asset._id} />}
            </Box>
            {/* Right area: template field cards + tab cards */}
            <Box sx={isFullPageLayout ? { minWidth: 0 } : { display: 'contents' }}>
            {/* Template Fields Section - moved before AI Description */}
            {selectedTemplate && (
              <Box>
                <TemplateFieldsForm
                  template={selectedTemplate}
                  values={templateFieldValues}
                  onChange={handleTemplateFieldChange}
                  disabled={!isEditing}
                  showUploadFields={false}
                  showEditFields={true}
                  groupBySeparators={true}
                  layoutVariant={layoutVariant}
                />
              </Box>
            )}

            {/* Full page: the two tab cards sit side by side. Panel: inert wrapper keeps original flow. */}
            {!isFullPageLayout && (
            <Box
              sx={isFullPageLayout ? {
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                alignItems: 'start',
              } : { display: 'contents' }}
            >
            {/* Tabbed Section - AI Description, Description & Tags, Versions */}
            <Box sx={{ 
              mb: 2,
              background: 'white',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden'
            }}>
              <Tabs 
                value={activeTab} 
                onChange={(event, newValue) => setActiveTab(newValue)}
                sx={{ 
                  background: '#f8f9fa',
                  borderBottom: '1px solid #e2e8f0',
                  minHeight: 'auto',
                  '& .MuiTab-root': {
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    padding: '2px 16px !important',
                    minHeight: '32px !important',
                    fontStretch: 'condensed',
                    letterSpacing: '-0.3px'
                  }
                }}
              >
                <Tab label="AI Beskrivning" />
                <Tab label="Beskrivning & Taggar" />
              </Tabs>

              {/* Tab Panel 0: AI Description */}
              {activeTab === 0 && (
                <Box sx={{ p: 2 }}>
                  {isEditing ? (
                    <TextField
                      name="aiDescription"
                      value={editableAIDescription}
                      onChange={(e) => setEditableAIDescription(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      placeholder="AI-genererad beskrivning"
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography sx={{ 
                      color: '#2d3748', 
                      fontSize: '0.85rem',
                      lineHeight: 1.3,
                      mb: 2
                    }}>
                      {asset.aiDescription || "Ingen AI-beskrivning tillgänglig."}
                    </Typography>
                  )}
                  
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>AI Taggar</Typography>
                  
                  {isEditing ? (
                    <>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Lägg till AI-tagg, tryck Enter..."
                        value={editableAITags.join(', ')}
                        onChange={(e) => setEditableAITags(e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag))}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {editableAITags.map((tag, index) => (
                          <Chip 
                            key={index} 
                            label={tag} 
                            size="small" 
                            onDelete={() => handleRemoveAITag(tag)}
                          />
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {asset.aiTags && asset.aiTags.length > 0 ? (
                        asset.aiTags.map((tag, index) => (
                          <Chip 
                            key={index} 
                            label={tag} 
                            size="small" 
                            variant="outlined" 
                            sx={{ bgcolor: '#f0f7ff' }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">Inga AI-taggar tillgängliga.</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab Panel 1: Description & Tags */}
              {activeTab === 1 && (
                <Box sx={{ p: 2 }}>
                  {/* Description */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Beskrivning:</Typography>
                    {isEditing ? (
                       <TextField
                           fullWidth
                           multiline
                           rows={4}
                           name="description"
                           value={editableMetadata.description || ''}
                           onChange={handleEditChange}
                           variant="outlined"
                           size="small"
                       />
                    ) : (
                        <Typography sx={{ 
                          color: '#2d3748', 
                          fontSize: '0.85rem',
                          lineHeight: 1.3
                        }}>
                          {asset.description || 'Ingen beskrivning'}
                        </Typography>
                    )}
                  </Box>

                  {/* Tags */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Taggar:</Typography>
                    {isEditing ? (
                       <Box>
                         {/* Tag input field */}
                         <TextField
                           fullWidth
                           placeholder="Skriv tag och tryck Enter"
                           value={tagInput}
                           onChange={handleTagInputChange}
                           onKeyDown={handleTagInputKeyDown}
                           variant="outlined"
                           size="small"
                           sx={{ mb: 1 }}
                           InputProps={{
                             endAdornment: (
                               <InputAdornment position="end">
                                 <IconButton 
                                   onClick={() => {
                                     if (tagInput.trim()) {
                                       if (!editableMetadata.tags?.includes(tagInput.trim())) {
                                         setEditableMetadata(prev => ({
                                           ...prev,
                                           tags: [...(prev.tags || []), tagInput.trim()]
                                         }));
                                       }
                                       setTagInput('');
                                     }
                                   }} 
                                   size="small"
                                 >
                                   <AddIcon />
                                 </IconButton>
                               </InputAdornment>
                             ),
                           }}
                         />
                         
                         {/* Display tags as chips in edit mode */}
                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                           {editableMetadata.tags && editableMetadata.tags.length > 0 ? 
                             editableMetadata.tags.map((tag, index) => (
                               <Chip 
                                 key={index} 
                                 label={tag} 
                                 size="small" 
                                 onDelete={() => handleRemoveTag(tag)}
                                 sx={{ mb: 0.5, bgcolor: '#f5f5f5' }}
                               />
                             )) : 
                             <Typography variant="body2">Inga taggar tillagda</Typography>
                           }
                         </Box>
                       </Box>
                    ) : (
                       <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                         {asset.tags && asset.tags.length > 0 ? 
                           asset.tags.map((tag, index) => (
                             <Chip 
                               key={index} 
                               label={tag} 
                               size="small" 
                               sx={{ mb: 0.5, bgcolor: '#f5f5f5' }}
                             />
                           )) : 
                           <Typography variant="body2">Inga taggar</Typography>
                         }
                       </Box>
                    )}
                  </Box>
                </Box>
              )}



            </Box>

            {/* Categories Section (moves to left rail in full page mode) */}
            {!isFullPageLayout && renderCategoriesSection()}

            {/* Versions, Exif and History Section */}
            <Paper elevation={0} sx={{ p: 0, mb: 3, border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
              <Tabs 
                value={historyActiveTab} 
                onChange={(event, newValue) => setHistoryActiveTab(newValue)}
                sx={{ 
                  background: '#f8f9fa',
                  borderBottom: '1px solid #e2e8f0',
                  minHeight: 'auto',
                  '& .MuiTab-root': {
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    minHeight: '40px',
                    textTransform: 'none',
                    color: '#64748b',
                    fontStretch: 'condensed',
                    letterSpacing: '-0.3px'
                  }
                }}
              >
                <Tab label="Versioner" />
                <Tab label="Metadata" />
                <Tab label="Historik" />
              </Tabs>

              {/* Tab Panel 0: Versions */}
              {historyActiveTab === 0 && (
                <Box sx={{ p: 2 }}>
                  <List dense={true}>
                    {/* First, show the current version (main asset file) */}
                    <ListItem 
                      sx={{ 
                        mb: 1, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '4px',
                        '&:hover': { bgcolor: '#f5f5f5' }
                      }}
                    >
                      {/* Current Version Thumbnail */}
                      <Box sx={{ mr: 2, flexShrink: 0 }}>
                        <Box
                          sx={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                          }}
                        >
                          <VersionThumbnailImage
                            src={currentVersionThumbnailSrc}
                            fallbackSrc={currentVersionFallbackSrc}
                            alt="Current version thumbnail"
                            placeholderLabel="CURRENT"
                          />
                        </Box>
                      </Box>

                      {/* Current Version Info - Two column layout */}
                      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        {/* Left column */}
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              Version {(asset.versions?.filter(v => v.versionId.toString() !== asset._id).length || 0) + 1}
                              <Chip 
                                label="Aktuell" 
                                size="small" 
                                sx={{ 
                                  ml: 1, 
                                  bgcolor: '#4caf50', 
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  height: '20px'
                                }} 
                              />
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {new Date(asset.uploadedAt).toLocaleString('sv-SE')}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                            {asset.currentVersionNote && asset.currentVersionNote.trim().length > 0
                              ? asset.currentVersionNote
                              : 'Aktuell version'}
                          </Typography>
                        </Box>
                        
                        {/* Right column */}
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            <strong>ID:</strong> {asset._id.toString()}
                          </Typography>
                          {asset.uploadedBy && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                              <strong>Skapad av:</strong> {uploaderLabel || 'Okänd'}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </ListItem>

                    {/* Then show all previous versions (exclude initial version to avoid duplication) */}
                    {asset.versions && asset.versions.length > 0 && (() => {
                      const filteredVersions = asset.versions
                        .filter((version) => version.versionId.toString() !== asset._id) // Exclude initial version with versionId === asset._id
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()); // Sort newest first (so they appear in descending order)
                      
                      return filteredVersions.map((version, index) => (
                        <ListItem 
                          key={version.versionId} 
                          sx={{ 
                            mb: 1, 
                            border: '1px solid #e0e0e0', 
                            borderRadius: '4px',
                            '&:hover': { bgcolor: '#f5f5f5' }
                          }}
                          secondaryAction={readOnly ? undefined : (
                            <IconButton edge="end" aria-label="restore" onClick={() => handleRestoreVersion(version)} disabled={restoring}>
                              {restoring ? <CircularProgress size={20} /> : <RestoreIcon />}
                            </IconButton>
                          )}
                        >
                          {/* Version Thumbnail */}
                          <Box sx={{ mr: 2, flexShrink: 0 }}>
                            <Box
                              sx={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden'
                              }}
                            >
                              <VersionThumbnailImage
                                src={
                                  buildStaticThumbnailUrl(version.thumbnailPath, version.createdAt || version.versionId)
                                  || (canUseVersionThumbnailEndpoint(version.filePath)
                                    ? buildVersionThumbnailUrl(asset._id, version.versionId, version.createdAt || version.versionId)
                                    : null)
                                }
                                fallbackSrc={
                                  version.thumbnailPath && canUseVersionThumbnailEndpoint(version.filePath)
                                    ? buildVersionThumbnailUrl(asset._id, version.versionId, version.createdAt || version.versionId)
                                    : null
                                }
                                alt={`Version ${index + 1} thumbnail`}
                                placeholderLabel={`V${index + 1}`}
                              />
                            </Box>
                          </Box>

                          {/* Version Info - Two column layout */}
                          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            {/* Left column */}
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  Version {filteredVersions.length - index}
                                  {/* Show "Original" chip for the oldest version */}
                                  {asset.versions && asset.versions.length > 0 && 
                                   index === filteredVersions.length - 1 && (
                                    <Chip 
                                      label="Original" 
                                      size="small" 
                                      sx={{ 
                                        ml: 1, 
                                        bgcolor: '#2196f3', 
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        height: '20px'
                                      }} 
                                    />
                                  )}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="textSecondary">
                                {new Date(version.createdAt).toLocaleString('sv-SE')}
                              </Typography>
                              {/* Show user comment (if any) and system note separately */}
                              {version.userComment && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {version.userComment}
                                </Typography>
                              )}
                              {version.note && (
                                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                  {version.note}
                                </Typography>
                              )}
                            </Box>
                            
                            {/* Right column */}
                            <Box>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                <strong>ID:</strong> {version.versionId.toString()}
                              </Typography>
                              {version.uploadedBy && (
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                  <strong>Skapad av:</strong> {getUploaderDisplayName(version.uploadedBy) || 'Okänd'}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </ListItem>
                      ));
                    })()}
                  </List>
                </Box>
              )}

              {/* Tab Panel 1: Metadata */}
              {historyActiveTab === 1 && (
                <Box sx={{ p: 2 }}>
                  {loadingExif && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Läser metadata…</Typography>
                    </Box>
                  )}
                  {!loadingExif && !exifData && (
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        if (!asset?._id) return;
                        setLoadingExif(true);
                        try {
                          const resp = await axiosInstance.get(`/api/assets/${asset._id}/metadata`);
                          setExifData(resp.data);
                        } catch (e: any) {
                          alert(e?.response?.data?.message || 'Kunde inte läsa metadata');
                        } finally {
                          setLoadingExif(false);
                        }
                      }}
                    >
                      Läs metadata
                    </Button>
                  )}
                  {!loadingExif && exifData && (
                    <Box>
                      {/* Clipping Path Info - always visible at top */}
                      {exifData.clippingPath && (
                        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', border: '1px solid #e2e8f0' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#334155' }}>
                            Urklippsbana
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            {exifData.clippingPath.present 
                              ? (exifData.clippingPath.name ? `${exifData.clippingPath.name}` : 'Ja (namn saknas)') 
                              : 'Nej'}
                          </Typography>
                        </Paper>
                      )}

                      {/* Two-column grid for metadata sections */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr', 
                        gap: 2 
                      }}>
                        {/* EXIF Section */}
                        {Object.keys(exifData.exif || {}).length > 0 && (
                          <Accordion defaultExpanded elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                            <AccordionSummary 
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ bgcolor: '#f8f9fa', minHeight: '48px' }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                                EXIF ({Object.keys(exifData.exif).length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <Table size="small">
                                <TableBody>
                                  {Object.entries(exifData.exif).map(([k, v]) => (
                                    <TableRow key={`exif-${k}`} hover>
                                      <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.813rem', 
                                        color: '#475569',
                                        width: '40%',
                                        wordBreak: 'break-word'
                                      }}>
                                        {k}
                                      </TableCell>
                                      <TableCell sx={{ 
                                        fontSize: '0.813rem', 
                                        color: '#64748b',
                                        wordBreak: 'break-word'
                                      }}>
                                        {String(v)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        )}

                        {/* IPTC Section */}
                        {Object.keys(exifData.iptc || {}).length > 0 && (
                          <Accordion defaultExpanded elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                            <AccordionSummary 
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ bgcolor: '#f8f9fa', minHeight: '48px' }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                                IPTC ({Object.keys(exifData.iptc).length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <Table size="small">
                                <TableBody>
                                  {Object.entries(exifData.iptc).map(([k, v]) => (
                                    <TableRow key={`iptc-${k}`} hover>
                                      <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.813rem', 
                                        color: '#475569',
                                        width: '40%',
                                        wordBreak: 'break-word'
                                      }}>
                                        {k}
                                      </TableCell>
                                      <TableCell sx={{ 
                                        fontSize: '0.813rem', 
                                        color: '#64748b',
                                        wordBreak: 'break-word'
                                      }}>
                                        {String(v)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        )}

                        {/* XMP Section */}
                        {Object.keys(exifData.xmp || {}).length > 0 && (
                          <Accordion elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                            <AccordionSummary 
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ bgcolor: '#f8f9fa', minHeight: '48px' }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                                XMP ({Object.keys(exifData.xmp).length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <Table size="small">
                                <TableBody>
                                  {Object.entries(exifData.xmp).map(([k, v]) => (
                                    <TableRow key={`xmp-${k}`} hover>
                                      <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.813rem', 
                                        color: '#475569',
                                        width: '40%',
                                        wordBreak: 'break-word'
                                      }}>
                                        {k}
                                      </TableCell>
                                      <TableCell sx={{ 
                                        fontSize: '0.813rem', 
                                        color: '#64748b',
                                        wordBreak: 'break-word'
                                      }}>
                                        {String(v)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Box>

                      {/* No data message */}
                      {Object.keys(exifData.exif || {}).length === 0 && 
                       Object.keys(exifData.iptc || {}).length === 0 && 
                       Object.keys(exifData.xmp || {}).length === 0 && (
                        <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa', border: '1px solid #e2e8f0' }}>
                          <Typography variant="body2" color="textSecondary">
                            Ingen EXIF/IPTC/XMP metadata hittades i denna bild.
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab Panel 2: History */}
              {historyActiveTab === 2 && (
                <Box sx={{ p: 2 }}>
                  <AssetHistory assetId={asset._id} />
                </Box>
              )}
            </Paper>

            {/* Collections Section (moves to left rail in full page mode) */}
            {!isFullPageLayout && renderCollectionsSection()}
            </Box>
            )}

            {/* Detailed Metadata Section (moves to left rail in full page mode) */}
            {!isFullPageLayout && renderDetailedMetadataSection()}
            {!isFullPageLayout && isAdmin && <VisualAssetProfilePanel assetId={asset._id} />}
            </Box>
            </Box>

          </Box>
        </>
      )}

      {/* Notification for sync operations */}
      <Snackbar
        open={syncMessage !== null}
        autoHideDuration={syncMessage?.duration || 5000}
        onClose={() => setSyncMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSyncMessage(null)} 
          severity={syncMessage?.type || 'info'}
          variant="filled"
        >
          {syncMessage?.text}
        </Alert>
      </Snackbar>

      <Dialog open={recipeCategoryDialogOpen} onClose={handleCloseRecipeCategoryDialog} fullWidth maxWidth="sm">
        <DialogTitle>Gör till ett recept</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Välj vilken underkategori under Recept bilden ska tillhöra.
          </Typography>

          {!recipeRootCategory ? (
            <Alert severity="error">
              Kategorin Recept kunde inte hittas.
            </Alert>
          ) : recipeSubcategories.length > 0 ? (
            <TextField
              select
              fullWidth
              label="Recept-underkategori"
              value={selectedRecipeSubCategoryId}
              onChange={(event) => setSelectedRecipeSubCategoryId(event.target.value)}
              disabled={convertingToRecipe}
              sx={{ mt: 1 }}
            >
              {recipeSubcategories.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Alert severity="info">
              Recept har inga underkategorier. Bilden flyttas till huvudkategorin Recept.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRecipeCategoryDialog} disabled={convertingToRecipe}>
            Avbryt
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConvertToRecipe}
            disabled={
              convertingToRecipe
              || !recipeRootCategory
              || (recipeSubcategories.length > 0 && !selectedRecipeSubCategoryId)
            }
          >
            {convertingToRecipe ? <CircularProgress size={20} color="inherit" /> : 'Gör till recept'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Upload Modal */}
      {asset && (
        <VersionUploadModal
          open={isVersionUploadOpen}
          onClose={() => {
            setIsVersionUploadOpen(false);
            setDroppedFile(null); // Clear dropped file when modal closes
          }}
          assetId={asset._id}
          initialFile={droppedFile}
          onUploadSuccess={() => {
            // Force preview refresh by clearing cache and timestamp
            if (previewImageUrl) {
              URL.revokeObjectURL(previewImageUrl);
            }
            setPreviewImageUrl(null);
            setFullscreenImageUrl(null);
            
            // Refresh asset details to show new version
            fetchAssetDetails(asset._id);
            setDroppedFile(null); // Clear dropped file after success
            
            // Force UI update by clearing viewing version
            setViewingVersion(null);
          }}
        />
      )}

      {/* Confirm Restore Dialog */}
      <Dialog open={confirmRestoreOpen} onClose={() => setConfirmRestoreOpen(false)}>
        <DialogTitle>Vill du återställa den här bilden?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Detta gör vald version till aktuell version och skapar en ny versionspost för återställningen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestoreOpen(false)} disabled={restoring}>Avbryt</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={restoring || !versionToRestore || !assetId}
            onClick={async () => {
              if (!versionToRestore || !assetId) return;
              setRestoring(true);
              try {
                const response = await axiosInstance.post(`/api/assets/${assetId}/versions/${versionToRestore.versionId}/restore`);
                const updatedAsset = response.data;
                const updatedAssetId = String(updatedAsset?._id || assetId);
                const updatedCategoryIds = Array.isArray(updatedAsset?.categories)
                  ? updatedAsset.categories
                      .map((categoryId: any) => String(categoryId?._id || categoryId || ''))
                      .filter(Boolean)
                  : [];
                window.setTimeout(() => {
                  try {
                    window.dispatchEvent(new CustomEvent('dam:upload-batch-completed', {
                      detail: {
                        assetIds: updatedAssetId ? [updatedAssetId] : [],
                        categoryIds: updatedCategoryIds,
                      },
                    }));
                  } catch {}
                }, 100);
                fetchAssetDetails(assetId);
                setViewingVersion(null);
                setSaveMessage('Version återställd!');
              } catch (err: any) {
                setSaveMessage(`Misslyckades att återställa version: ${err.message}`);
              } finally {
                setRestoring(false);
                setConfirmRestoreOpen(false);
                setVersionToRestore(null);
              }
            }}
          >
            Ja
          </Button>
        </DialogActions>
      </Dialog>
      <RelationImpactDialog
        open={recipeDeleteImpactDialogOpen}
        action="delete"
        asset={asset}
        wordPressImpacts={deleteWordPressImpacts}
        onClose={() => {
          setRecipeDeleteImpactDialogOpen(false);
          setDeleteWordPressImpacts([]);
        }}
        onConfirm={deleteAssetDirectly}
        onConfirmWithWordPressUnpublish={handleDeleteWithWordPressUnpublish}
      />
    </Box>
  );
};

export default AssetDetails; 